console.log('📦 bundle-builder.js loading...');

/**
 * Bundle Builder Component - Refactored
 * Single source of truth for state management
 * Unified render pipeline for all UI updates
 */
class BundleBuilder extends HTMLElement {
  constructor() {
    super();

    // SINGLE STATE - this is the only source of truth
    this.state = {
      selectedProducts: new Map(), // variantId -> {productId, variantId, title, price, handle, quantity, sellingPlans}
      selectedSubscription: null,  // {id, type, discount}
    };

    this.sectionId = this.dataset.sectionId;
    const shopifyGlobal = typeof window !== 'undefined' ? window.Shopify : undefined;
    const detectedCurrency = shopifyGlobal && shopifyGlobal.currency
      ? (typeof shopifyGlobal.currency === 'string'
          ? shopifyGlobal.currency
          : (shopifyGlobal.currency.active || shopifyGlobal.currency.current))
      : undefined;
    this.moneyFormat = this.dataset.moneyFormat
      || (shopifyGlobal && shopifyGlobal.money_format)
      || '{{amount}}';
    this.moneyWithCurrencyFormat = this.dataset.moneyWithCurrencyFormat
      || (shopifyGlobal && shopifyGlobal.money_with_currency_format)
      || this.moneyFormat;
    this.currencyCode = this.dataset.currencyCode
      || detectedCurrency
      || 'USD';
    this.config = null;
    this.tiers = [];
    this.subscriptions = [];
    this.products = [];

    // Cache for DOM elements
    this.elements = {};
    
    // Event unsubscribers
    this.cartUpdateUnsubscriber = null;
  }
  
  disconnectedCallback() {
    // Clean up event listeners
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
      this.cartUpdateUnsubscriber = null;
    }
  }

  connectedCallback() {
    console.log('🚀 BundleBuilder connectedCallback');

    if (window.requestIdleCallback) {
      requestIdleCallback(() => this.initialize());
    } else {
      setTimeout(() => this.initialize(), 0);
    }
  }

  initialize() {
    console.log('⚙️ Initializing BundleBuilder');

    // Load configuration
    this.loadConfiguration();
    if (!this.config) {
      console.error('❌ No config found');
      return;
    }

    // Cache all DOM elements once
    this.cacheElements();
    if (!this.validateElements()) {
      console.error('❌ Required elements missing');
      return;
    }

    // Initialize defaults
    this.initializeDefaults();

    // Bind all events once
    this.bindEvents();

    // Listen to cart removal events to sync bundle builder state
    this.bindCartSyncEvents();

    // Sync with cart on page load (add cart items to stack)
    this.syncCartToStack().then(() => {
      // Initial render after cart sync
      this.render();
    console.log('✅ BundleBuilder initialized');
    }).catch(err => {
      console.warn('Cart sync failed, proceeding with initialization:', err);
      // Still render even if sync fails
      this.render();
      console.log('✅ BundleBuilder initialized');
    });
  }

  loadConfiguration() {
    const data = window.bundleBuilderData?.[this.sectionId];
    if (!data) return;

    this.config = data.config;
    this.tiers = data.tiers || [];
    this.subscriptions = data.subscriptions || [];
    this.products = data.products || [];
  }

  cacheElements() {
    // Cache all DOM elements in one place
    const selectors = {
      // Product cards
      productCards: '.bundle-product-card',

      // Right panel - product stack
      productStackItems: '.product-stack__items',

      // Right panel - progress bar (old structure)
      progressBarFill: '.progress-bar-fill',
      milestoneMarkers: '.milestone-marker',
      
      // New bundle save progress bar
      bundleSaveProgressFill: '.bundle-save-progress-fill',
      productCountButtons: '.product-count-btn',

      // Right panel - buy button
      buyButton: '.buy-now-button',

      // Left summary (if exists - for old structure)
      selectedProductsList: '.selected-products-list',
      subtotalElement: '.subtotal-amount',
      bundleDiscountRow: '.bundle-discount-row',
      bundleDiscountPercent: '.bundle-discount-percent',
      bundleDiscountAmount: '.bundle-discount-amount',
      subscriptionDiscountRow: '.subscription-discount-row',
      subscriptionDiscountPercent: '.subscription-discount-percent',
      subscriptionDiscountAmount: '.subscription-discount-amount',
      totalElement: '.total-amount',
      totalSavingsRow: '.total-savings',
      savingsAmount: '.savings-amount',
      savingsPercent: '.savings-percent',
      addToCartButton: '.bundle-add-to-cart',

      // Progress (old structure - kept for compatibility)
      progressMessage: '.progress-text',
      progressSubtext: '.progress-subtext'
    };

    Object.entries(selectors).forEach(([key, selector]) => {
      if (selector.includes(' ')) {
        // Specific selector like '.bundle-summary-panel .progress-bar-fill'
        this.elements[key] = this.querySelector(selector);
      } else {
        // General selector - might return multiple elements
        const els = this.querySelectorAll(selector);
        this.elements[key] = els.length === 1 ? els[0] : els;
      }
      
      // Debug logging for key elements
      if (['buyButton', 'progressBarFill', 'productStackItems'].includes(key)) {
        console.log(`🔍 Cached ${key}:`, {
          selector,
          found: !!this.elements[key],
          element: this.elements[key]
        });
      }
    });
  }

  validateElements() {
    // Only require the right panel elements (new structure)
    return !!(
      this.elements.productStackItems &&
      this.elements.buyButton
    );
  }

  initializeDefaults() {
    // Initialize subscription toggle - always open by default
    this.initializeSubscriptionToggle();
    
    // Set up subscription radio buttons with proper data attributes
    this.setupSubscriptionRadios();
    
    // Set up default subscription selection (First radio selected by default)
    this.setupDefaultSubscriptionSelection();
    
    // Ensure first radio is selected and state is updated
    const firstRadio = this.querySelector('.frequency-radio, .subscription-option-radio');
    if (firstRadio) {
      if (!firstRadio.checked) {
        firstRadio.checked = true;
      }
      // Update state with first subscription
      this.handleSubscriptionChange(firstRadio);
    } else {
      // Fallback: initialize to one-time only if no subscription radios exist
      this.state.selectedSubscription = {
        id: 'onetime',
        type: 'onetime',
        discount: 0
      };
    }
    
    // Update initial subscription toggle text
    this.updateSubscriptionToggleText(this.state.selectedSubscription);
  }

  initializeSubscriptionToggle() {
    const subscriptionToggle = this.querySelector(`#subscription-toggle-${this.sectionId}`);
    const subscriptionOptions = this.querySelector(`#subscription-options-${this.sectionId}`);
    const subscriptionAddToCartWrapper = this.querySelector(`#subscription-add-to-cart-wrapper-${this.sectionId}`);

    if (subscriptionToggle && subscriptionOptions) {
      // Toggle is checked by default (Subscription enabled on load)
      subscriptionToggle.checked = true;
      subscriptionOptions.style.display = 'grid';
      if (subscriptionAddToCartWrapper) {
        subscriptionAddToCartWrapper.style.display = 'block';
      }

      // Select first subscription radio by default
      const firstRadio = this.querySelector('.frequency-radio, .subscription-option-radio');
      if (firstRadio) {
        firstRadio.checked = true;
        this.handleSubscriptionChange(firstRadio);
      } else {
        // Fallback to monthly if no first radio found
        this.selectMonthlySubscription();
      }

      // Enable toggle functionality - allow users to toggle subscription options
      subscriptionToggle.addEventListener('change', (e) => {
        if (e.target.checked) {
          // Toggle turned ON - show subscription options
          subscriptionOptions.style.display = 'grid';
          if (subscriptionAddToCartWrapper) {
            subscriptionAddToCartWrapper.style.display = 'block';
          }
          // Select first subscription when toggle is turned on
          const firstRadio = this.querySelector('.frequency-radio, .subscription-option-radio');
          if (firstRadio) {
            firstRadio.checked = true;
            this.handleSubscriptionChange(firstRadio);
          }
        } else {
          // Toggle turned OFF - hide subscription options
          subscriptionOptions.style.display = 'none';
          if (subscriptionAddToCartWrapper) {
            subscriptionAddToCartWrapper.style.display = 'none';
          }
          // Set state to one-time purchase
          this.state.selectedSubscription = {
            id: 'onetime',
            type: 'onetime',
            discount: 0
          };
          // Re-render to update pricing
          this.render();
        }
      });
    }
  }

  setupSubscriptionRadios() {
    const subscriptionRadios = this.querySelectorAll('.subscription-option-radio, .frequency-radio');
    
    subscriptionRadios.forEach(radio => {
      // Ensure subscription discount is set (from Liquid template or fallback to 0)
      if (!radio.dataset.subscriptionDiscount) {
        const discount = radio.getAttribute('data-subscription-discount') || '0';
        radio.dataset.subscriptionDiscount = discount;
      }
      
      // Set subscription type based on value if not already set
      if (!radio.dataset.subscriptionType) {
        radio.dataset.subscriptionType = radio.value;
      }
      if (!radio.dataset.subscriptionId) {
        radio.dataset.subscriptionId = radio.value;
      }
      
      // Debug logging
      console.log('📋 Subscription radio setup:', {
        value: radio.value,
        subscriptionId: radio.dataset.subscriptionId,
        subscriptionType: radio.dataset.subscriptionType,
        subscriptionDiscount: radio.dataset.subscriptionDiscount
      });
    });
  }

  setupDefaultSubscriptionSelection() {
    // Set up the first subscription radio (Monthly) to be selected when toggle is on
    const firstSubscriptionRadio = this.querySelector('.frequency-radio, .subscription-option-radio');
    if (firstSubscriptionRadio) {
      // Get discount from data attribute (from blocks) - ensure it's properly parsed
      let discount = parseFloat(firstSubscriptionRadio.dataset.subscriptionDiscount);
      if (isNaN(discount)) {
        discount = parseFloat(firstSubscriptionRadio.getAttribute('data-subscription-discount')) || 0;
      }
      
      this.monthlySubscription = {
        id: firstSubscriptionRadio.dataset.subscriptionId || firstSubscriptionRadio.value || 'monthly',
        type: firstSubscriptionRadio.dataset.subscriptionType || firstSubscriptionRadio.value || 'monthly',
        discount: discount
      };
      
      console.log('📋 Default subscription setup:', {
        radio: firstSubscriptionRadio,
        discount: discount,
        dataAttribute: firstSubscriptionRadio.dataset.subscriptionDiscount,
        subscription: this.monthlySubscription
      });
    }
  }

  selectMonthlySubscription() {
    // Find and select the Monthly subscription radio
    const monthlyRadio = this.querySelector('.frequency-radio[value="monthly"], .subscription-option-radio[value="monthly"]');
    if (monthlyRadio) {
      monthlyRadio.checked = true;
      
      // Get discount from data attribute (from blocks) - ensure it's properly parsed
      let discount = parseFloat(monthlyRadio.dataset.subscriptionDiscount);
      if (isNaN(discount)) {
        discount = parseFloat(monthlyRadio.getAttribute('data-subscription-discount')) || 0;
      }
      
      this.state.selectedSubscription = {
        id: monthlyRadio.dataset.subscriptionId || 'monthly',
        type: monthlyRadio.dataset.subscriptionType || 'monthly',
        discount: discount
      };
      
      console.log('📋 Monthly subscription selected:', {
        radio: monthlyRadio,
        discount: discount,
        dataAttribute: monthlyRadio.dataset.subscriptionDiscount,
        subscription: this.state.selectedSubscription
      });
      
      this.updateSubscriptionToggleText(this.state.selectedSubscription);
      this.render();
    }
  }

  bindEvents() {
    // Check if we should use custom element event handling
    // data-use-inline="true" means inline scripts are handling events
    const useInline = this.dataset.useInline === 'true';

    if (useInline) {
      console.log('⚠️ BundleBuilder: Using inline scripts for event handling');
      return;
    }

    console.log('✅ BundleBuilder: Using custom element for event handling');

    // Use event delegation for all clicks
    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('change', this.handleChange.bind(this));
  }

  bindCartSyncEvents() {
    // Listen for bundle items removed from cart
    document.addEventListener('bundle-items-removed', this.handleBundleItemsRemoved.bind(this));
    
    // Also listen to cart update events to sync state
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Only sync if the update came from outside bundle builder
        if (event.source && event.source !== 'bundle-builder' && event.source !== 'bundle-builder-remove') {
          // Only sync if bundle items are actually involved in this cart update
          // Check if cartData contains bundle items or if we need to check fresh cart
          if (event.cartData && event.cartData.items) {
            const hasBundleItems = event.cartData.items.some(item => 
              item.properties && 
              (item.properties._subscription_bundle_product === 'true' || 
               item.properties._subscription_bundle_product === true ||
               item.properties._bundle_id)
            );
            
            // Only sync if bundle items are present in the cart
            // This prevents syncing when only non-bundle products are removed
            if (hasBundleItems) {
              this.syncWithCart(event.cartData);
            } else {
              // If no bundle items in cart, check if we have bundle items in state
              // If we do, we should clear them (cart is empty of bundles)
              if (this.state.selectedProducts.size > 0) {
                // Fetch fresh cart to be sure before clearing
                this.syncWithCartFresh();
              }
            }
          } else {
            // If cartData is missing or incomplete, fetch fresh cart data
            this.syncWithCartFresh();
          }
        }
      });
    }
  }

  handleBundleItemsRemoved(event) {
    const { variantIds, cartData } = event.detail;
    console.log('🔄 Bundle items removed from cart, syncing bundle builder:', variantIds);
    console.log('🔄 Current bundle builder state keys:', Array.from(this.state.selectedProducts.keys()));
    
    // Normalize variant IDs to numbers (Map keys are variantIds as numbers)
    const normalizedVariantIds = variantIds.map(id => parseInt(id));
    
    // Remove items from bundle builder state - Map key IS the variantId
    let hasChanges = false;
    
    normalizedVariantIds.forEach(variantId => {
      // Try both as number and as string key (in case of type mismatch)
      if (this.state.selectedProducts.has(variantId)) {
        this.state.selectedProducts.delete(variantId);
        hasChanges = true;
        console.log(`🔄 Removed variant ${variantId} from bundle builder`);
      } else if (this.state.selectedProducts.has(variantId.toString())) {
        this.state.selectedProducts.delete(variantId.toString());
        hasChanges = true;
        console.log(`🔄 Removed variant ${variantId} (as string) from bundle builder`);
      } else {
        // Try to find by iterating (fallback)
        this.state.selectedProducts.forEach((product, key) => {
          const keyVariantId = parseInt(key);
          const productVariantId = parseInt(product.variantId);
          if (keyVariantId === variantId || productVariantId === variantId) {
            this.state.selectedProducts.delete(key);
            hasChanges = true;
            console.log(`🔄 Removed variant ${variantId} (found by iteration, key: ${key}) from bundle builder`);
          }
        });
      }
    });
    
    console.log('🔄 Has changes:', hasChanges);
    console.log('🔄 Remaining bundle builder state keys:', Array.from(this.state.selectedProducts.keys()));
    
    // Always sync with cart data if available, then render
    if (cartData && cartData.items) {
      const syncHasChanges = this.syncWithCart(cartData);
      hasChanges = hasChanges || syncHasChanges;
    }
    
    // Always render if we made changes
    if (hasChanges) {
      console.log('🔄 Re-rendering bundle builder after removal');
      this.render();
    } else {
      console.log('🔄 No changes detected, skipping render');
    }
  }

  async syncCartToStack() {
    try {
      // Fetch current cart
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        console.warn('Failed to fetch cart for stack sync');
        return;
      }
      
      const cart = await cartResponse.json();
      
      // Find all bundle items in cart
      const bundleItems = cart.items.filter(item => 
        item.properties && 
        (item.properties._subscription_bundle_product === 'true' || 
         item.properties._subscription_bundle_product === true ||
         item.properties._bundle_id)
      );
      
      if (bundleItems.length === 0) {
        console.log('📦 No bundle items in cart to sync');
        return;
      }
      
      console.log(`📦 Syncing ${bundleItems.length} bundle items from cart to stack...`);
      
      // Extract subscription type from first bundle item (they should all have the same type)
      const firstBundleItem = bundleItems[0];
      const subscriptionType = firstBundleItem.properties?._subscription_type || 'onetime';
      
      // Set subscription type if available
      if (subscriptionType && subscriptionType !== 'onetime') {
        const matchingSubscription = this.subscriptions.find(sub => 
          sub.type === subscriptionType || 
          sub.type === subscriptionType.toLowerCase()
        );
        
        if (matchingSubscription) {
          this.state.selectedSubscription = {
            id: matchingSubscription.id,
            type: matchingSubscription.type,
            discount: matchingSubscription.discountPercentage || 0
          };
          console.log(`📦 Set subscription type to: ${subscriptionType}`);
          
          // Ensure subscription toggle is checked
          const subscriptionToggle = this.querySelector(`#subscription-toggle-${this.sectionId}`);
          if (subscriptionToggle) {
            subscriptionToggle.checked = true;
            // Show subscription options
            const subscriptionOptions = this.querySelector(`#subscription-options-${this.sectionId}`);
            if (subscriptionOptions) {
              subscriptionOptions.style.display = 'grid';
            }
            const subscriptionAddToCartWrapper = this.querySelector(`#subscription-add-to-cart-wrapper-${this.sectionId}`);
            if (subscriptionAddToCartWrapper) {
              subscriptionAddToCartWrapper.style.display = 'block';
            }
          }
          
          // Update subscription radio button
          const subscriptionRadio = this.querySelector(`input[value="${subscriptionType}"], input[value="${subscriptionType.toLowerCase()}"]`);
          if (subscriptionRadio) {
            subscriptionRadio.checked = true;
            // Trigger change event to update UI
            subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
          }
          
          // Update subscription toggle text if method exists
          if (typeof this.updateSubscriptionToggleText === 'function') {
            this.updateSubscriptionToggleText(this.state.selectedSubscription);
          }
        }
      } else {
        // If onetime, ensure toggle is off
        const subscriptionToggle = this.querySelector(`#subscription-toggle-${this.sectionId}`);
        if (subscriptionToggle) {
          subscriptionToggle.checked = false;
          const subscriptionOptions = this.querySelector(`#subscription-options-${this.sectionId}`);
          if (subscriptionOptions) {
            subscriptionOptions.style.display = 'none';
          }
        }
      }
      
      // Add each bundle item to the stack
      let itemsAdded = 0;
      for (const cartItem of bundleItems) {
        const cartVariantId = parseInt(cartItem.variant_id);
        const cartProductId = parseInt(cartItem.product_id);
        const quantity = cartItem.quantity || 1;
        
        // Find product card in DOM - try by variant_id first, then search selling plans
        // This handles the case where 3-month subscription variant is in cart but we need base product card
        let productCard = this.querySelector(`[data-variant-id="${cartVariantId}"]`);
        let baseVariantId = cartVariantId; // Default to cart variant_id
        
        // If not found by variant_id, search through all product cards' selling plans
        // Reverse engineer: when adding to cart, we use sellingPlanData.variant_id for 3-month
        // So when syncing from cart, we need to find which product card has a selling plan with this variant_id
        if (!productCard) {
          // Search all product cards to find one with a selling plan that matches cart variant_id
          const allProductCards = this.querySelectorAll('.bundle-product-card');
          
          for (const card of allProductCards) {
            try {
              const sellingPlansData = card.dataset.sellingPlans;
              if (sellingPlansData && sellingPlansData !== '[]') {
                const sellingPlans = JSON.parse(sellingPlansData);
                
                // Check if any selling plan has variant_id matching the cart variant_id
                const matchingPlan = sellingPlans.find(plan => {
                  const planVariantId = plan.variant_id || plan.variantId;
                  return planVariantId === cartVariantId;
                });
                
                if (matchingPlan) {
                  // Found the product card! Use its base variant_id (from data-variant-id attribute)
                  productCard = card;
                  baseVariantId = parseInt(card.dataset.variantId);
                  console.log(`📦 Found base product card via selling plan search: cart variant ${cartVariantId} -> base variant ${baseVariantId}`, {
                    matchingPlan: matchingPlan,
                    productCard: card
                  });
                  break;
                }
              }
            } catch (e) {
              console.warn('Error parsing selling plans while searching for base variant:', e);
            }
          }
          
          // Fallback: if still not found, try by product_id (for products without selling plans in DOM)
          if (!productCard && cartProductId) {
            productCard = this.querySelector(`[data-product-id="${cartProductId}"]`);
            if (productCard) {
              // Use the base variant_id from the product card (single month version)
              baseVariantId = parseInt(productCard.dataset.variantId);
              console.log(`📦 Found base product card by product_id (fallback): cart variant ${cartVariantId} -> base variant ${baseVariantId}`);
            }
          }
        }
        
        // Check if already in stack (using base variant_id)
        if (this.state.selectedProducts.has(baseVariantId)) {
          // Update quantity if different
          const existing = this.state.selectedProducts.get(baseVariantId);
          if (existing.quantity !== quantity) {
            existing.quantity = Math.min(quantity, 10);
            itemsAdded++;
            console.log(`📦 Updated quantity for variant ${baseVariantId} to ${quantity}`);
          }
          continue;
        }
        
        // Find product data from configuration or DOM
        let productData = null;
        let productImage = null;
        
        if (productCard) {
          const imageElement = productCard.querySelector('.product-card__image img');
          if (imageElement) {
            productImage = imageElement.src || imageElement.getAttribute('src');
          }
        }
        
        // Always try to get data from product card in DOM first (if available)
        // This ensures we use the same data source as handleProductAdd()
        let sellingPlans = [];
        let productCardPrice = null;
        let productCardCompareAtPrice = null;
        let productCardProductId = null;
        let productCardTitle = null;
        let productCardHandle = null;
        
        if (productCard) {
          try {
            const sellingPlansData = productCard.dataset.sellingPlans;
            if (sellingPlansData && sellingPlansData !== '[]') {
              sellingPlans = JSON.parse(sellingPlansData);
              console.log(`📦 Found selling plans from DOM for base variant ${baseVariantId}:`, sellingPlans);
            }
          } catch (e) {
            console.warn('Error parsing selling plans for cart sync:', e);
          }
          
          // Extract price data from product card (same as handleProductAdd)
          // This is the base product card data (single month version)
          productCardPrice = productCard.dataset.productPrice ? parseInt(productCard.dataset.productPrice) : null;
          productCardCompareAtPrice = productCard.dataset.productCompareAtPrice ? parseInt(productCard.dataset.productCompareAtPrice) : null;
          productCardProductId = productCard.dataset.productId ? parseInt(productCard.dataset.productId) : null;
          productCardTitle = productCard.dataset.productTitle || null;
          productCardHandle = productCard.dataset.productHandle || null;
        }
        
        // First, try to find in products array from config (using base variant_id)
        const configProduct = this.products.find(p => p.variantId === baseVariantId);
        if (configProduct) {
          // Use image from DOM if found, otherwise try cart item image, otherwise empty
          let imageUrl = productImage || '';
          if (!imageUrl) {
            // Try cart item image (might be string or object)
            if (cartItem.image) {
              imageUrl = typeof cartItem.image === 'string' ? cartItem.image : (cartItem.image.src || cartItem.image.url || '');
            }
            // Try featured_image as fallback
            if (!imageUrl && cartItem.featured_image) {
              imageUrl = typeof cartItem.featured_image === 'string' ? cartItem.featured_image : (cartItem.featured_image.src || cartItem.featured_image.url || '');
            }
          }
          
          // Prioritize product card data for prices (same as handleProductAdd)
          // Use base variant_id (single month version) for the stack
          productData = {
            productId: productCardProductId || configProduct.id,
            variantId: baseVariantId, // Use base variant_id (single month), not cart variant_id
            title: productCardTitle || configProduct.title,
            price: productCardPrice !== null ? productCardPrice : (configProduct.price || cartItem.price || 0),
            compareAtPrice: productCardCompareAtPrice !== null ? productCardCompareAtPrice : (configProduct.compareAtPrice || cartItem.compare_at_price || 0),
            handle: productCardHandle || configProduct.handle,
            quantity: Math.min(quantity, 10),
            sellingPlans: sellingPlans, // Use selling plans from DOM
            image: imageUrl
          };
        } else if (productCard) {
          // Product card found in DOM (data already extracted above)
          // Use base variant_id (single month version) for the stack
          productData = {
            productId: productCardProductId || cartItem.product_id,
            variantId: baseVariantId, // Use base variant_id (single month), not cart variant_id
            title: productCardTitle || cartItem.product_title,
            price: productCardPrice !== null ? productCardPrice : (cartItem.price || 0),
            compareAtPrice: productCardCompareAtPrice !== null ? productCardCompareAtPrice : (cartItem.compare_at_price || 0),
            handle: productCardHandle || cartItem.handle || '',
            quantity: Math.min(quantity, 10),
            sellingPlans: sellingPlans, // Use selling plans extracted above
            image: productImage || ''
          };
        } else {
          // Fallback: use cart item data directly
          // Note: This won't have selling plans, but at least we can add the product
          let imageUrl = '';
          // Try cart item image (might be string or object)
          if (cartItem.image) {
            if (typeof cartItem.image === 'string') {
              imageUrl = cartItem.image;
            } else if (cartItem.image.src) {
              imageUrl = cartItem.image.src;
            } else if (cartItem.image.url) {
              imageUrl = cartItem.image.url;
            }
          }
          // Try featured_image as fallback
          if (!imageUrl && cartItem.featured_image) {
            if (typeof cartItem.featured_image === 'string') {
              imageUrl = cartItem.featured_image;
            } else if (cartItem.featured_image.src) {
              imageUrl = cartItem.featured_image.src;
            } else if (cartItem.featured_image.url) {
              imageUrl = cartItem.featured_image.url;
            }
          }
          
          // Use base variant_id if we found it, otherwise use cart variant_id
          productData = {
            productId: cartItem.product_id,
            variantId: baseVariantId, // Use base variant_id if found, otherwise cart variant_id
            title: cartItem.product_title,
            price: cartItem.price,
            compareAtPrice: cartItem.compare_at_price || 0,
            handle: cartItem.handle || '',
            quantity: Math.min(quantity, 10),
            sellingPlans: sellingPlans, // Use selling plans if found (empty array if not)
            image: imageUrl
          };
        }
        
        // Add to selected products using base variant_id
        this.state.selectedProducts.set(baseVariantId, productData);
        itemsAdded++;
        console.log(`📦 Added to stack: cart variant ${cartVariantId} -> base variant ${baseVariantId} (${productData.title}) with quantity ${productData.quantity}`, {
          cartVariantId: cartVariantId,
          baseVariantId: baseVariantId,
          price: this.formatMoney(productData.price),
          compareAtPrice: this.formatMoney(productData.compareAtPrice || 0),
          priceSource: productCardPrice !== null ? 'productCard' : (configProduct ? 'configProduct' : 'cartItem'),
          compareAtPriceSource: productCardCompareAtPrice !== null ? 'productCard' : (configProduct ? 'configProduct' : 'cartItem'),
          image: productData.image || 'NO IMAGE',
          imageSource: productImage ? 'DOM' : (cartItem.image ? 'cartItem.image' : (cartItem.featured_image ? 'cartItem.featured_image' : 'none')),
          sellingPlansCount: productData.sellingPlans?.length || 0,
          sellingPlans: productData.sellingPlans || []
        });
      }
      
      if (itemsAdded > 0) {
        console.log(`✅ Synced ${itemsAdded} items from cart to stack`);
      }
    } catch (error) {
      console.error('Error syncing cart to stack:', error);
    }
  }

  async syncWithCartFresh() {
    // Fetch fresh cart data to avoid stale data issues
    try {
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        console.warn('Failed to fetch fresh cart for sync');
        return false;
      }
      const freshCartData = await cartResponse.json();
      return this.syncWithCart(freshCartData);
    } catch (error) {
      console.error('Error fetching fresh cart for sync:', error);
      return false;
    }
  }

  syncWithCart(cartData) {
    if (!cartData || !cartData.items || cartData.items.length === 0) {
      // If cart is empty, clear all items
      if (this.state.selectedProducts.size > 0) {
        this.state.selectedProducts.clear();
        console.log('🔄 Cart is empty, cleared bundle builder state');
        return true; // Return true to indicate changes were made
      }
      return false;
    }
    
    // Get variant IDs currently in cart (only bundle items) - normalize to numbers
    const cartVariantIds = new Set();
    cartData.items.forEach(item => {
      // Only consider bundle items
      if (item.properties && 
          (item.properties._subscription_bundle_product === 'true' || 
           item.properties._subscription_bundle_product === true ||
           item.properties._bundle_id)) {
        // Normalize to number for comparison
        cartVariantIds.add(parseInt(item.variant_id));
      }
    });
    
    console.log('🔄 Cart variant IDs (bundle items):', Array.from(cartVariantIds));
    console.log('🔄 Bundle builder Map keys:', Array.from(this.state.selectedProducts.keys()));
    console.log('🔄 Bundle builder variant IDs:', Array.from(this.state.selectedProducts.values()).map(p => p.variantId));
    
    // If no bundle items in cart but we have items in state, clear the state
    if (cartVariantIds.size === 0 && this.state.selectedProducts.size > 0) {
      console.log('🔄 No bundle items in cart, clearing bundle builder state');
      this.state.selectedProducts.clear();
      return true;
    }
    
    // Remove items from bundle builder that are not in cart
    // Map key IS the variantId (as number), so we can check directly
    let hasChanges = false;
    const itemsToRemove = [];
    
    this.state.selectedProducts.forEach((product, key) => {
      // Normalize variant ID to number for comparison (key should already be number)
      const keyVariantId = typeof key === 'number' ? key : parseInt(key);
      
      // Check if this variant is still in cart - Map key IS the variantId
      if (!cartVariantIds.has(keyVariantId)) {
        itemsToRemove.push(key);
        hasChanges = true;
        console.log(`🔄 Marking variant ${keyVariantId} (key: ${key}, type: ${typeof key}) for removal - not in cart`);
      }
    });
    
    // Remove items that are no longer in cart
    itemsToRemove.forEach(key => {
      this.state.selectedProducts.delete(key);
      console.log(`🔄 Removed item with key: ${key}`);
    });
    
    if (hasChanges) {
      console.log('🔄 Synced bundle builder with cart, removed items:', itemsToRemove.length);
    } else {
      console.log('🔄 No changes needed - bundle builder already in sync with cart');
    }
    
    return hasChanges; // Return whether changes were made
  }

  handleClick(event) {
    // Product add button
    if (event.target.closest('.bundle-product-add-btn')) {
      event.preventDefault();
      this.handleProductAdd(event.target.closest('.bundle-product-add-btn'));
      return;
    }

    // Quantity increase
    if (event.target.closest('.quantity-increase')) {
      event.preventDefault();
      const btn = event.target.closest('.quantity-increase');
      const variantId = parseInt(btn.dataset.variantId);
      this.increaseQuantity(variantId);
      return;
    }

    // Quantity decrease
    if (event.target.closest('.quantity-decrease')) {
      event.preventDefault();
      const btn = event.target.closest('.quantity-decrease');
      const variantId = parseInt(btn.dataset.variantId);
      this.decreaseQuantity(variantId);
      return;
    }

    // Stack item quantity increase
    if (event.target.closest('.stack-item-quantity-increase')) {
      event.preventDefault();
      const btn = event.target.closest('.stack-item-quantity-increase');
      const variantId = parseInt(btn.dataset.variantId);
      this.increaseQuantity(variantId);
      return;
    }

    // Stack item quantity decrease
    if (event.target.closest('.stack-item-quantity-decrease')) {
      event.preventDefault();
      const btn = event.target.closest('.stack-item-quantity-decrease');
      const variantId = parseInt(btn.dataset.variantId);
      this.decreaseQuantity(variantId);
      return;
    }

    // Remove from stack
    if (event.target.closest('.stack-item-remove')) {
      event.preventDefault();
      const btn = event.target.closest('.stack-item-remove');
      const variantId = parseInt(btn.dataset.variantId);
      this.removeProduct(variantId);
      return;
    }

    // Remove from summary
    if (event.target.closest('.selected-product-remove')) {
      event.preventDefault();
      const btn = event.target.closest('.selected-product-remove');
      const variantId = parseInt(btn.dataset.variantId);
      this.removeProduct(variantId);
      return;
    }

    // Remove from card
    if (event.target.closest('.product-remove-btn')) {
      event.preventDefault();
      const btn = event.target.closest('.product-remove-btn');
      const variantId = parseInt(btn.dataset.variantId);
      this.removeProduct(variantId);
      return;
    }

    // Premade bundle
    if (event.target.closest('.premade-bundle-add-btn')) {
      event.preventDefault();
      this.handlePremadeBundleAdd(event.target.closest('.premade-bundle-add-btn'));
      return;
    }

    // Subscription add to cart button (uses currently selected subscription from dropdown)
    if (event.target.closest('#subscription-add-to-cart-' + this.sectionId)) {
      event.preventDefault();
      // Get the currently selected subscription radio button to ensure we use the latest selection
      const selectedRadio = this.querySelector('.frequency-radio:checked, .subscription-option-radio:checked');
      if (selectedRadio) {
        // Update state from the selected radio to ensure it's current
        this.handleSubscriptionChange(selectedRadio);
      }
      // Ensure we're using subscription (not one-time)
      event.isOneTimePurchase = false;
      this.handleAddToCart(event);
      return;
    }

    // One-time purchase button (adds to cart WITHOUT subscription/selling plan)
    if (event.target.closest('#one-time-purchase-' + this.sectionId)) {
      event.preventDefault();
      // Pass a flag to handleAddToCart to indicate this is a one-time purchase
      event.isOneTimePurchase = true;
      this.handleAddToCart(event);
      return;
    }

    // Add to cart (old structure) - always use subscription
    if (event.target.closest('.bundle-add-to-cart, .buy-now-button')) {
      event.preventDefault();
      // Ensure we're using subscription (not one-time) for the add to cart button
      event.isOneTimePurchase = false;
      
      // Ensure first radio is selected if none is selected
      const selectedRadio = this.querySelector('.frequency-radio:checked, .subscription-option-radio:checked');
      if (!selectedRadio) {
        const firstRadio = this.querySelector('.frequency-radio, .subscription-option-radio');
        if (firstRadio) {
          firstRadio.checked = true;
          this.handleSubscriptionChange(firstRadio);
        }
      }
      
      this.handleAddToCart(event);
      return;
    }
  }

  handleChange(event) {
    // Tier selection
    if (event.target.classList.contains('tier-radio')) {
      this.handleTierChange(event.target);
      return;
    }

    // Subscription/frequency selection
    if (event.target.classList.contains('frequency-radio') || event.target.classList.contains('subscription-option-radio')) {
      this.handleSubscriptionChange(event.target);
      return;
    }
  }

  // ==============================================
  // STATE MUTATIONS - Only place state can change
  // ==============================================

  addProduct(productData) {
    console.log('➕ addProduct called with:', productData);
    this.state.selectedProducts.set(productData.variantId, {
      ...productData,
      quantity: productData.quantity || 1
    });
    console.log('➕ Product added to state, calling render');
    this.render();
  }

  removeProduct(variantId) {
    this.state.selectedProducts.delete(variantId);
    this.render();
  }

  setQuantity(variantId, quantity) {
    const product = this.state.selectedProducts.get(variantId);
    if (!product) return;

    if (quantity <= 0) {
      this.removeProduct(variantId);
    } else {
      product.quantity = Math.min(Math.max(quantity, 1), 10);
      this.render();
    }
  }

  increaseQuantity(variantId) {
    const product = this.state.selectedProducts.get(variantId);
    if (!product) return;

    const currentTotal = this.getTotalItemCount();
    if (currentTotal >= this.config.maxProducts) {
      this.showNotification(`Maximum ${this.config.maxProducts} total items allowed`);
      return;
    }

    if (product.quantity >= 10) {
      this.showNotification('Maximum quantity per product is 10');
      return;
    }

    this.setQuantity(variantId, product.quantity + 1);
  }

  decreaseQuantity(variantId) {
    const product = this.state.selectedProducts.get(variantId);
    if (!product) return;

    this.setQuantity(variantId, product.quantity - 1);
  }

  setSubscription(subscription) {
    this.state.selectedSubscription = subscription;
    this.updateSubscriptionToggleText(subscription);
    // Full render will update subscription pricing
    this.render();
  }

  updateSubscriptionToggleText(subscription) {
    const toggleText = this.querySelector('.toggle-text');
    if (toggleText) {
      if (subscription && subscription.discount > 0) {
        toggleText.textContent = `Subscribe & Save ${subscription.discount}%`;
      } else if (subscription && subscription.type === 'onetime') {
        // One-Time Purchase - show maximum available discount from cards
        this.updateSubscriptionToggleTextFromCards();
      } else {
        // Show maximum available discount from cards
        this.updateSubscriptionToggleTextFromCards();
      }
    }
  }

  updateSubscriptionToggleTextFromCards() {
    const toggleText = this.querySelector('.toggle-text');
    if (!toggleText) return;
    
    // Get maximum savings percentage from dynamically calculated subscription cards
    const subscriptionCards = this.querySelectorAll('.subscription-option-card');
    let maxSavingsPercent = 0;
    
    subscriptionCards.forEach(card => {
      const savingsEl = card.querySelector('.subscription-card-savings');
      if (savingsEl && savingsEl.textContent.trim()) {
        // Extract percentage from text like "Save Extra 15%" or "15%"
        const savingsText = savingsEl.textContent.trim();
        const match = savingsText.match(/(\d+(?:\.\d+)?)%/);
        if (match) {
          const percent = parseFloat(match[1]);
          if (percent > maxSavingsPercent) {
            maxSavingsPercent = percent;
          }
        }
      }
    });
    
    // Update toggle text with maximum savings
    if (maxSavingsPercent > 0) {
      toggleText.textContent = `Subscribe & Save up to ${maxSavingsPercent.toFixed(0)}%`;
    } else {
      toggleText.textContent = 'Subscribe & Save';
    }
  }

  getMaxSubscriptionDiscount() {
    // Get maximum discount from subscription options
    const subscriptionRadios = this.querySelectorAll('.subscription-option input[type="radio"]');
    let maxDiscount = 0;
    
    subscriptionRadios.forEach(radio => {
      const discount = parseFloat(radio.dataset.subscriptionDiscount) || 0;
      if (discount > maxDiscount) {
        maxDiscount = discount;
      }
    });
    
    return maxDiscount;
  }

  // ==============================================
  // EVENT HANDLERS
  // ==============================================

  handleProductAdd(button) {
    const card = button.closest('.bundle-product-card');
    if (!card) return;

    const variantId = parseInt(card.dataset.variantId);

    // Check if already in bundle
    if (this.state.selectedProducts.has(variantId)) {
      this.increaseQuantity(variantId);
      return;
    }

    // Check max limit
    const currentTotal = this.getTotalItemCount();
    if (currentTotal >= this.config.maxProducts) {
      this.showNotification(`Maximum ${this.config.maxProducts} total items allowed`);
      return;
    }

    // Parse selling plans
    let sellingPlans = [];
    try {
      const sellingPlansData = card.dataset.sellingPlans;
      if (sellingPlansData && sellingPlansData !== '[]') {
        sellingPlans = JSON.parse(sellingPlansData);
      }
    } catch (e) {
      console.error('Error parsing selling plans:', e);
    }

    // Add product
    this.addProduct({
      productId: parseInt(card.dataset.productId),
      variantId: variantId,
      title: card.dataset.productTitle,
      price: parseInt(card.dataset.productPrice),
      compareAtPrice: parseInt(card.dataset.productCompareAtPrice) || 0,
      handle: card.dataset.productHandle,
      quantity: 1,
      sellingPlans: sellingPlans,
      image: card.querySelector('.product-card__image img').src
    });
  }

  handlePremadeBundleAdd(button) {
    let card = button.closest('.premade-bundle-card');
    if (!card) {
      const bundleId = button.dataset.bundleId;
      if (bundleId) {
        card = this.querySelector(`.premade-bundle-card[data-bundle-id="${bundleId}"]`)
          || document.querySelector(`.premade-bundle-card[data-bundle-id="${bundleId}"]`);
      }
    }
    if (!card) return;

    // Check if master product is available - if so, use quick-view modal instead of adding to stack
    const masterProductHandle = card.dataset.masterProductHandle;
    
    if (masterProductHandle) {
      // Trigger the quick-view modal like the card-product does
      const popupOverlay = document.getElementById('popupOverlay');
      
      // Show loading animation (hide text, show spinner)
      button.disabled = true;
      button.classList.add('loading');
      
      // Call fetchProductDetails directly to avoid triggering the event listener's spinner
      // The event listener in quick-view-modal.liquid shows a spinner, but we want to use our own
      const popupContent = document.getElementById('popupContent');
      
      if (popupContent) {
        // Manually call fetchProductDetails logic to avoid duplicate spinners
        const fetchUrl = `/products/${masterProductHandle}?view=quick-view`;
        
        fetch(fetchUrl)
          .then(response => {
            if (!response.ok) {
              throw new Error(`HTTP error! Status: ${response.status}`);
            }
            return response.text();
          })
          .then(htmlContent => {
            const responseHTML = new DOMParser().parseFromString(htmlContent, 'text/html');
            const productElement = responseHTML.querySelector('.product-custom-template');
            
            if (productElement) {
              popupContent.innerHTML = productElement.innerHTML;
              
              // Reinitialize scripts if needed
              const scripts = popupContent.querySelectorAll('script');
              scripts.forEach(oldScriptTag => {
                const newScriptTag = document.createElement('script');
                Array.from(oldScriptTag.attributes).forEach(attribute => {
                  newScriptTag.setAttribute(attribute.name, attribute.value);
                });
                newScriptTag.textContent = oldScriptTag.textContent;
                oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
              });
              
              // Wait a bit for scripts to execute, then reinitialize event listeners
              // This ensures all scripts from the quick-view template have loaded
              setTimeout(() => {
                // Try to call reinitializeEventListeners if it exists (from quick-view-modal.liquid)
                if (typeof reinitializeEventListeners === 'function') {
                  reinitializeEventListeners();
                } else {
                  // Fallback: manually reinitialize event listeners like quick-view-modal.liquid does
                  const labels = popupContent.querySelectorAll('.single_product');
                  const customForm = popupContent.querySelector(".pdp-form-btns");
                  
                  if (labels && customForm) {
                    labels.forEach(function (label) {
                      label.addEventListener('click', function () {
                        if (label.dataset.id === "Default Title") {
                          customForm.classList.remove('hide-buynow');
                        } else {
                          customForm.classList.add('hide-buynow');
                        }
                      });
                    });
                  }
                  
                  // Call enableAddToCart and initSubscription if they exist (from subscription-scripts.js.liquid)
                  if (typeof enableAddToCart === 'function') {
                    enableAddToCart();
                  }
                  if (typeof initSubscription === 'function') {
                    initSubscription();
                  }
                }
                
                // Hide loading animation when modal content is loaded and initialized
                button.disabled = false;
                button.classList.remove('loading');
              }, 100);
              
              if (popupOverlay) {
                document.body.classList.add('popup-open', 'overflow-hidden-mobile');
                popupOverlay.style.display = 'block';
              }
            }
          })
          .catch(error => {
            console.error('Error opening quick view for master product:', error);
            // Hide loading animation on error
            button.disabled = false;
            button.classList.remove('loading');
          });
      } else {
        // Fallback: Use temp button if popupContent doesn't exist
        const tempButton = document.createElement('button');
        tempButton.className = 'quick-view-modal open-quick-view add--to--cart';
        tempButton.setAttribute('data-handle', masterProductHandle);
        // Make it invisible so no duplicate UI
        tempButton.style.position = 'absolute';
        tempButton.style.opacity = '0';
        tempButton.style.pointerEvents = 'none';
        tempButton.style.width = '1px';
        tempButton.style.height = '1px';
        
        // Append to body temporarily so event delegation works
        document.body.appendChild(tempButton);
        
        // Trigger the click event
        tempButton.click();
        
        // Monitor for modal opening to hide loading state
        const checkModalOpen = setInterval(() => {
          if (popupOverlay && popupOverlay.style.display === 'block') {
            button.disabled = false;
            button.classList.remove('loading');
            clearInterval(checkModalOpen);
            setTimeout(() => tempButton.remove(), 100);
          }
        }, 100);
        
        // Fallback: remove loading after timeout if modal doesn't open
        setTimeout(() => {
          clearInterval(checkModalOpen);
          button.disabled = false;
          button.classList.remove('loading');
          tempButton.remove();
        }, 5000);
      }
      
      return;
    }

    // Fallback to old behavior if no master product (for backwards compatibility)
    let bundleProducts = [];
    try {
      bundleProducts = JSON.parse(card.dataset.bundleProducts);
    } catch (e) {
      this.showNotification('Failed to load bundle data');
      return;
    }

    const currentTotal = this.getTotalItemCount();
    const bundleTotalItems = bundleProducts.reduce((sum, p) => sum + p.quantity, 0);

    if (currentTotal + bundleTotalItems > this.config.maxProducts) {
      this.showNotification(
        `Adding this bundle would exceed the maximum of ${this.config.maxProducts} total items.`
      );
      return;
    }

    // Add all products from bundle
    bundleProducts.forEach(productData => {
      const variantId = productData.variantId;

      console.log('productData', productData);
      if (this.state.selectedProducts.has(variantId)) {
        const existingProduct = this.state.selectedProducts.get(variantId);
        existingProduct.quantity = Math.min(existingProduct.quantity + productData.quantity, 10);
        // Update image if not already set
        if (!existingProduct.image && productData.image) {
          existingProduct.image = productData.image;
        }
      } else {
        this.state.selectedProducts.set(variantId, {
          productId: productData.productId,
          variantId: productData.variantId,
          title: productData.title,
          price: productData.price,
          compareAtPrice: productData.compareAtPrice || 0,
          handle: productData.handle,
          quantity: Math.min(productData.quantity, 10),
          sellingPlans: productData.sellingPlans || [],
          image: productData.image || ''
        });
      }
    });

    this.render();

    // Show success feedback
    const bundleTitle = card.querySelector('.premade-bundle-card__title')?.textContent || 'Bundle';
    this.showNotification(`✓ ${bundleTitle} added!`, 'success');

    button.textContent = '✓ Added';
    button.classList.add('added');
    button.disabled = true;
    setTimeout(() => {
      button.textContent = '+ Add To Cart';
      button.classList.remove('added');
      button.disabled = false;
    }, 2000);

    // Scroll to product stack to show items were added
    const productStack = document.querySelector('.product-stack__container');
    if (productStack) {
      setTimeout(() => {
        const yOffset = -100;
        const y = productStack.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }, 300);
    }
  }

  handleSubscriptionChange(radio) {
    // Get discount from data attribute (set in Liquid from blocks)
    let discount = parseFloat(radio.dataset.subscriptionDiscount) || 0;
    
    // Fallback: Try to extract from attribute string if dataset doesn't work
    if (discount === 0 || isNaN(discount)) {
      const attrValue = radio.getAttribute('data-subscription-discount');
      if (attrValue) {
        discount = parseFloat(attrValue) || 0;
      }
    }
    
    // Last resort: Extract discount from the option text if not in data attributes
    if (discount === 0 || isNaN(discount)) {
      const discountText = radio.nextElementSibling?.querySelector('.option-discount')?.textContent;
      if (discountText) {
        const match = discountText.match(/(\d+)%/);
        if (match) {
          discount = parseFloat(match[1]);
        }
      }
    }
    
    const subscription = {
      id: radio.dataset.subscriptionId || radio.value,
      type: radio.dataset.subscriptionType || radio.value,
      discount: discount
    };
    
    console.log('🔄 Subscription changed:', {
      radio: radio,
      subscription: subscription,
      dataAttributes: {
        subscriptionId: radio.dataset.subscriptionId,
        subscriptionType: radio.dataset.subscriptionType,
        subscriptionDiscount: radio.dataset.subscriptionDiscount
      }
    });
    
    this.setSubscription(subscription);
  }

  handleTierChange(radio) {
    // Tiers are auto-calculated, but keeping this for manual override if needed
    const tier = this.tiers.find(t => t.id === radio.dataset.tierId);
    if (tier) {
      this.render();
    }
  }

  async removePreviousBundleItems() {
    try {
      // Fetch the current cart to find bundle items
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        console.warn('Failed to fetch cart for bundle removal');
        return;
      }
      
      let cart = await cartResponse.json();
      
      // Find all items with _subscription_bundle_product property
      const bundleItems = cart.items.filter(item => 
        item.properties && 
        (item.properties._subscription_bundle_product === 'true' || 
         item.properties._subscription_bundle_product === true ||
         item.properties._bundle_id)
      );
      
      if (bundleItems.length === 0) {
        console.log('No previous bundle items found in cart');
        return;
      }
      
      console.log(`Removing ${bundleItems.length} previous bundle items from cart`, bundleItems);
      
      // Use cart/update.js with updates object for more reliable removal
      // Build updates object: { line_number: 0 } for each bundle item
      // IMPORTANT: Shopify API expects string keys for line numbers
      const updates = {};
      
      // Create a map of item keys to line numbers (1-based index)
      cart.items.forEach((item, index) => {
        const isBundleItem = item.properties && 
          (item.properties._subscription_bundle_product === 'true' || 
           item.properties._subscription_bundle_product === true ||
           item.properties._bundle_id);
        
        if (isBundleItem) {
          // Use string key explicitly - Shopify API expects string line numbers
          const lineNumber = index + 1;
          updates[String(lineNumber)] = 0; // Set quantity to 0 to remove
        }
      });
      
      if (Object.keys(updates).length === 0) {
        console.log('No bundle items to remove');
        return;
      }
      
      console.log('Removing bundle items with updates:', updates);
      console.log('Updates object keys type check:', Object.keys(updates).map(k => ({ key: k, type: typeof k })));
      
      // Try using cart/update.js endpoint first (batch removal)
      // Include cart-icon-bubble section to update cart count
      const cartUpdateUrl = (typeof routes !== 'undefined' && routes.cart_update_url) 
        ? routes.cart_update_url 
        : '/cart/update.js';
      
      let updateResult;
      let removalSuccessful = false;
      
      try {
        const updateResponse = await fetch(cartUpdateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            updates,
            sections: ['cart-icon-bubble'],
            sections_url: window.location.pathname
          })
        });
        
        if (!updateResponse.ok) {
          const errorData = await updateResponse.json().catch(() => ({}));
          throw new Error(`Failed to remove bundle items: ${errorData.message || updateResponse.statusText}`);
        }
        
        updateResult = await updateResponse.json();
        console.log('Batch removal response:', updateResult);
        
        // Update cart icon bubble from response if available
        if (updateResult.sections && updateResult.sections['cart-icon-bubble']) {
          this.updateCartIconBubble(updateResult.sections['cart-icon-bubble']);
        }
        
        // Verify removal using the response data
        if (updateResult.items) {
          const remainingAfterBatch = updateResult.items.filter(item => 
            item.properties && 
            (item.properties._subscription_bundle_product === 'true' || 
             item.properties._subscription_bundle_product === true ||
             item.properties._bundle_id)
          );
          
          if (remainingAfterBatch.length === 0) {
            removalSuccessful = true;
            console.log('✅ Batch removal successful - all bundle items removed');
          } else {
            console.warn(`⚠️ Batch removal incomplete - ${remainingAfterBatch.length} items remain, trying sequential removal`);
            removalSuccessful = false;
          }
        } else {
          // If no items in response, verify by fetching fresh cart
          const verifyResponse = await fetch('/cart.js');
          if (verifyResponse.ok) {
            const verifyCart = await verifyResponse.json();
            const remaining = verifyCart.items.filter(item => 
              item.properties && 
              (item.properties._subscription_bundle_product === 'true' || 
               item.properties._subscription_bundle_product === true ||
               item.properties._bundle_id)
            );
            
            if (remaining.length === 0) {
              removalSuccessful = true;
              console.log('✅ Batch removal successful - verified via fresh cart fetch');
            } else {
              console.warn(`⚠️ Batch removal incomplete - ${remaining.length} items remain after verification, trying sequential removal`);
              removalSuccessful = false;
            }
          } else {
            // If verification fails, assume batch worked but log warning
            console.warn('⚠️ Could not verify batch removal - proceeding with assumption of success');
            removalSuccessful = true;
          }
        }
      } catch (error) {
        console.warn('Batch removal failed, trying sequential removal:', error);
      }
      
      // Fallback to sequential removal if batch didn't work
      // Must be sequential because line numbers shift as items are removed
      if (!removalSuccessful) {
        console.log('Using sequential removal method (most reliable)...');
        
        // Get all bundle item line numbers from original cart
        const bundleItemLines = [];
        cart.items.forEach((item, index) => {
          const isBundleItem = item.properties && 
            (item.properties._subscription_bundle_product === 'true' || 
             item.properties._subscription_bundle_product === true ||
             item.properties._bundle_id);
          if (isBundleItem) {
            bundleItemLines.push(index + 1); // Store line numbers
          }
        });
        
        // Sort by line number descending - remove from highest to lowest
        // This way, when we remove line 5, line 4 doesn't become line 5 (it stays line 4)
        const sortedLines = [...bundleItemLines].sort((a, b) => b - a);
        
        console.log(`Removing ${sortedLines.length} items sequentially (highest to lowest line numbers)...`);
        
        // Remove items sequentially (one at a time) to handle line number shifting
        for (const line of sortedLines) {
          try {
            const response = await fetch('/cart/change.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                line: line,
                quantity: 0,
                sections: ['cart-icon-bubble'],
                sections_url: window.location.pathname
              })
            });
            
            if (response.ok) {
              const result = await response.json();
              updateResult = result; // Keep the latest result
              
              // Update cart icon bubble from each response
              if (result.sections && result.sections['cart-icon-bubble']) {
                this.updateCartIconBubble(result.sections['cart-icon-bubble']);
              }
            } else {
              console.warn(`Failed to remove item at line ${line}`);
            }
          } catch (error) {
            console.warn(`Error removing item at line ${line}:`, error);
          }
        }
        
        // Verify all items were removed by fetching fresh cart
        const verifyCartResponse = await fetch('/cart.js');
        if (verifyCartResponse.ok) {
          const verifyCart = await verifyCartResponse.json();
          const remainingBundleItems = verifyCart.items.filter(item => 
            item.properties && 
            (item.properties._subscription_bundle_product === 'true' || 
             item.properties._subscription_bundle_product === true ||
             item.properties._bundle_id)
          );
          
          if (remainingBundleItems.length > 0) {
            console.warn(`⚠️ ${remainingBundleItems.length} bundle items still remain after sequential removal. Retrying...`);
            
            // Retry removal for remaining items using current line numbers
            const retryLines = [];
            verifyCart.items.forEach((item, index) => {
              if (item.properties && 
                  (item.properties._subscription_bundle_product === 'true' || 
                   item.properties._subscription_bundle_product === true ||
                   item.properties._bundle_id)) {
                retryLines.push(index + 1);
              }
            });
            
            // Sort descending and remove sequentially
            const sortedRetryLines = [...retryLines].sort((a, b) => b - a);
            for (const line of sortedRetryLines) {
              try {
                const retryResponse = await fetch('/cart/change.js', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    line: line,
                    quantity: 0,
                    sections: ['cart-icon-bubble'],
                    sections_url: window.location.pathname
                  })
                });
                
                if (retryResponse.ok) {
                  const retryResult = await retryResponse.json();
                  updateResult = retryResult;
                  
                  if (retryResult.sections && retryResult.sections['cart-icon-bubble']) {
                    this.updateCartIconBubble(retryResult.sections['cart-icon-bubble']);
                  }
                }
              } catch (error) {
                console.warn(`Retry failed for line ${line}:`, error);
              }
            }
            
            // Final verification
            const finalVerifyResponse = await fetch('/cart.js');
            if (finalVerifyResponse.ok) {
              const finalVerifyCart = await finalVerifyResponse.json();
              const finalRemaining = finalVerifyCart.items.filter(item => 
                item.properties && 
                (item.properties._subscription_bundle_product === 'true' || 
                 item.properties._subscription_bundle_product === true ||
                 item.properties._bundle_id)
              );
              
              if (finalRemaining.length > 0) {
                console.error(`❌ Still ${finalRemaining.length} bundle items remaining after retry. Manual cleanup may be needed.`);
              } else {
                console.log('✅ All bundle items successfully removed after retry');
              }
              
              updateResult = finalVerifyCart;
            }
          } else {
            console.log('✅ All bundle items successfully removed');
            updateResult = verifyCart;
          }
        }
      }
      
      // Update cart drawer and cart icon bubble using renderContents (same as product-form.js)
      const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      
      // Check if we can use renderContents - it requires sections to be present
      let canUseRenderContents = updateResult && 
                                 cartDrawer && 
                                 typeof cartDrawer.renderContents === 'function' &&
                                 updateResult.sections &&
                                 typeof updateResult.sections === 'object';
      
      if (canUseRenderContents) {
        // Check if cart drawer has getSectionsToRender method to verify required sections
        let hasRequiredSections = true;
        if (typeof cartDrawer.getSectionsToRender === 'function') {
          const requiredSections = cartDrawer.getSectionsToRender();
          // Check if all required sections are present in the response
          // Note: renderContents uses section.id to access sections, but the actual key might be section.section
          hasRequiredSections = requiredSections.every(section => {
            // Check both section.id and section.section as keys (cart-drawer.js uses section.id)
            return updateResult.sections[section.id] || updateResult.sections[section.section];
          });
        }
        
        if (hasRequiredSections) {
          try {
            await cartDrawer.renderContents(updateResult);
            console.log('✅ Cart drawer and cart icon bubble updated via renderContents (remove)');
          } catch (renderError) {
            console.warn('Error in renderContents, falling back to manual refresh:', renderError);
            // Fall through to fallback
            canUseRenderContents = false;
          }
        } else {
          console.log('Required sections not present in response, using fallback');
          canUseRenderContents = false;
        }
      }
      
      if (!canUseRenderContents) {
        // Fallback: manually update cart icon bubble
        if (updateResult && updateResult.sections && updateResult.sections['cart-icon-bubble']) {
          this.updateCartIconBubble(updateResult.sections['cart-icon-bubble']);
        } else {
          await this.refreshCartIconBubble();
        }
        // Refresh cart drawer
        await this.refreshCartDrawer();
      }
      
      // Trigger cart update event (non-blocking)
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { 
          source: 'bundle-builder-remove',
          cartData: updateResult
        });
      }
      
    } catch (error) {
      console.error('Error removing previous bundle items:', error);
      // Don't throw - continue with adding new bundle even if removal fails
      // But log the error so we can debug
    }
  }

  async handleAddToCart(event) {
    const products = Array.from(this.state.selectedProducts.values());
    const totalItems = this.getTotalItemCount();

    // Get minimum required items from the first discount tier, fallback to config
    let minRequired = this.config.minProducts || 2;
    if (this.tiers && this.tiers.length > 0) {
      // Sort tiers by minItems and get the first one
      const sortedTiers = [...this.tiers].sort((a, b) => a.minItems - b.minItems);
      minRequired = sortedTiers[0].minItems;
    }

    if (totalItems < minRequired) {
      this.showNotification(`Please add at least ${minRequired} items`);
      return;
    }

    // Find the button that triggered this event (could be subscription, one-time, or old structure)
    const button = event.target.closest(
      `#subscription-add-to-cart-${this.sectionId}, #one-time-purchase-${this.sectionId}, .bundle-add-to-cart, .buy-now-button`
    );
    if (!button) return;

    // Disable both buttons to prevent multiple clicks, but only show loading on the clicked button
    const subscriptionButton = this.querySelector(`#subscription-add-to-cart-${this.sectionId}`);
    const oneTimeButton = this.querySelector(`#one-time-purchase-${this.sectionId}`);
    
    // Disable both buttons
    if (subscriptionButton) {
      subscriptionButton.disabled = true;
    }
    if (oneTimeButton) {
      oneTimeButton.disabled = true;
    }
    
    // Only add loading class to the button that was actually clicked
    button.classList.add('loading');
    
    // Store original text from the button's text span (not the entire button content)
    const textSpan = button.querySelector('.btn-text, .one-time-purchase-text');
    const originalText = textSpan ? textSpan.textContent : button.textContent;
    
    // Only update the text span, not the entire button (to preserve price spans)
    if (textSpan) {
      textSpan.textContent = 'Adding...';
    } else {
      // Fallback if text span doesn't exist
    button.textContent = 'Adding...';
    }

    try {
      // Remove previous bundle items before adding new ones
      await this.removePreviousBundleItems();
      const bundleId = `bundle_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const pricing = this.calculatePricing();
      
      // Check if this is a one-time purchase (from event flag or button ID)
      const isOneTimePurchase = event.isOneTimePurchase || button.id === `one-time-purchase-${this.sectionId}`;
      
      let subscription;
      if (isOneTimePurchase) {
        subscription = {
          id: 'onetime',
          type: 'onetime',
          discount: 0
        };
      } else {
        // For subscription button, ensure we're using the currently selected subscription
        // Read directly from the selected radio button to get the latest value
        let selectedRadio = this.querySelector('.frequency-radio:checked, .subscription-option-radio:checked');
        
        // If no radio is checked, select the default monthly subscription
        if (!selectedRadio) {
          console.log('⚠️ No subscription radio selected, selecting default monthly');
          // Check if subscription toggle is on
          const subscriptionToggle = this.querySelector(`#subscription-toggle-${this.sectionId}`);
          if (subscriptionToggle && subscriptionToggle.checked) {
            // Select monthly as default
            this.selectMonthlySubscription();
            selectedRadio = this.querySelector('.frequency-radio:checked, .subscription-option-radio:checked');
          }
        }
        
        if (selectedRadio) {
          // Get subscription data from the selected radio button
          let discount = parseFloat(selectedRadio.dataset.subscriptionDiscount) || 0;
          if (discount === 0 || isNaN(discount)) {
            const attrValue = selectedRadio.getAttribute('data-subscription-discount');
            if (attrValue) {
              discount = parseFloat(attrValue) || 0;
            }
          }
          
          // Get subscription type - normalize to lowercase for comparison
          let subscriptionType = selectedRadio.dataset.subscriptionType || selectedRadio.value;
          if (subscriptionType) {
            subscriptionType = subscriptionType.toLowerCase();
          }
          
          subscription = {
            id: selectedRadio.dataset.subscriptionId || selectedRadio.value,
            type: subscriptionType || selectedRadio.value,
            discount: discount
          };
          
          // Ensure it's not one-time (subscription button should never use one-time)
          // Also check if subscription toggle is on - if off, we shouldn't be here but handle it
          const subscriptionToggle = this.querySelector(`#subscription-toggle-${this.sectionId}`);
          const isToggleOn = subscriptionToggle && subscriptionToggle.checked;
          
          if (!isToggleOn) {
            console.warn('⚠️ Subscription button clicked but toggle is off. Enabling toggle and selecting monthly.');
            if (subscriptionToggle) {
              subscriptionToggle.checked = true;
              const subscriptionOptions = this.querySelector(`#subscription-options-${this.sectionId}`);
              if (subscriptionOptions) {
                subscriptionOptions.style.display = 'grid';
              }
            }
            this.selectMonthlySubscription();
            selectedRadio = this.querySelector('.frequency-radio:checked, .subscription-option-radio:checked');
            if (selectedRadio) {
              let newDiscount = parseFloat(selectedRadio.dataset.subscriptionDiscount) || 0;
              if (newDiscount === 0 || isNaN(newDiscount)) {
                const newAttrValue = selectedRadio.getAttribute('data-subscription-discount');
                if (newAttrValue) {
                  newDiscount = parseFloat(newAttrValue) || 0;
                }
              }
              let newType = selectedRadio.dataset.subscriptionType || selectedRadio.value;
              if (newType) {
                newType = newType.toLowerCase();
              }
              subscription = {
                id: selectedRadio.dataset.subscriptionId || selectedRadio.value,
                type: newType || selectedRadio.value,
                discount: newDiscount
              };
            }
          } else if (!subscription.type || subscription.type === 'onetime') {
            console.warn('⚠️ Subscription button clicked but got one-time subscription, using monthly as fallback');
            // Use monthly subscription as fallback
            const monthlyRadio = this.querySelector('.frequency-radio[value="monthly"], .subscription-option-radio[value="monthly"]');
            if (monthlyRadio) {
              monthlyRadio.checked = true;
              let monthlyDiscount = parseFloat(monthlyRadio.dataset.subscriptionDiscount) || 0;
              if (monthlyDiscount === 0 || isNaN(monthlyDiscount)) {
                const monthlyAttrValue = monthlyRadio.getAttribute('data-subscription-discount');
                if (monthlyAttrValue) {
                  monthlyDiscount = parseFloat(monthlyAttrValue) || 0;
                }
              }
              subscription = {
                id: monthlyRadio.dataset.subscriptionId || monthlyRadio.value || 'monthly',
                type: monthlyRadio.dataset.subscriptionType || monthlyRadio.value || 'monthly',
                discount: monthlyDiscount
              };
            } else {
              // Last resort: use first available subscription radio
              const firstRadio = this.querySelector('.frequency-radio, .subscription-option-radio');
              if (firstRadio) {
                firstRadio.checked = true;
                let firstDiscount = parseFloat(firstRadio.dataset.subscriptionDiscount) || 0;
                if (firstDiscount === 0 || isNaN(firstDiscount)) {
                  const firstAttrValue = firstRadio.getAttribute('data-subscription-discount');
                  if (firstAttrValue) {
                    firstDiscount = parseFloat(firstAttrValue) || 0;
                  }
                }
                subscription = {
                  id: firstRadio.dataset.subscriptionId || firstRadio.value,
                  type: firstRadio.dataset.subscriptionType || firstRadio.value,
                  discount: firstDiscount
                };
              }
            }
          }
          
          // Update state to keep it in sync
          this.state.selectedSubscription = subscription;
        } else {
          // Last resort: subscription button was clicked but no subscription available
          // This shouldn't happen if subscription toggle is on, but handle it gracefully
          console.error('❌ Subscription button clicked but no subscription radio found. This should not happen.');
          throw new Error('No subscription option available. Please select a subscription plan.');
        }
      }
      
      console.log('🛒 Adding to cart:', {
        isOneTimePurchase,
        subscriptionType: subscription?.type,
        subscriptionId: subscription?.id,
        buttonId: button.id,
        hasSellingPlan: subscription?.type && subscription.type !== 'onetime'
      });

      const items = products.map((productData) => {
        let variantIdToUse = productData.variantId;
        let sellingPlanId = null;

        // Only add selling plan if subscription is selected and it's not one-time purchase
        if (subscription && subscription.type && subscription.type !== 'onetime') {
          console.log(`🔍 Looking for selling plan for ${productData.title}:`, {
            subscriptionType: subscription.type,
            hasSellingPlans: !!productData.sellingPlans,
            sellingPlansCount: productData.sellingPlans?.length || 0,
            sellingPlans: productData.sellingPlans
          });
          
          const sellingPlanData = this.findMatchingSellingPlan(productData, subscription.type);
          
          if (sellingPlanData) {
            // Try multiple possible ID properties
            sellingPlanId = sellingPlanData.id || sellingPlanData.selling_plan_id || sellingPlanData.sellingPlanId;
            
            if (!sellingPlanId) {
              console.error(`❌ Selling plan found but no ID property:`, sellingPlanData);
            }
            
            if (sellingPlanData.variant_id) {
              variantIdToUse = sellingPlanData.variant_id;
            }
            console.log(`✅ Found selling plan for ${productData.title}:`, {
              subscriptionType: subscription.type,
              sellingPlanId: sellingPlanId,
              variantId: variantIdToUse,
              sellingPlanData: sellingPlanData
            });
          } else {
            console.warn(`⚠️ No selling plan found for ${productData.title} with subscription type: ${subscription.type}`, {
              availableSellingPlans: productData.sellingPlans?.map(sp => ({ name: sp.name, id: sp.id || sp.selling_plan_id || sp.sellingPlanId })) || []
            });
          }
        } else {
          console.log(`ℹ️ One-time purchase for ${productData.title} - no selling plan`);
        }
        // If subscription.type is 'onetime' or null, sellingPlanId remains null (one-time purchase)

        const item = {
          id: variantIdToUse,
          quantity: productData.quantity,
          properties: {
            '_bundle_id': bundleId,
            '_bundle_name': this.config.title || 'Custom Bundle',
            '_bundle_size': `${totalItems} items`,
            '_bundle_discount': `${pricing.bundleDiscount}%`,
            '_product_name': productData.title,
            '_subscription_bundle_product': 'true'
          }
        };

        if (sellingPlanId) {
          item.selling_plan = sellingPlanId;
          item.properties._subscription_type = subscription.type;
          item.properties._subscription_discount = `${pricing.subscriptionDiscount}%`;
          console.log(`✅ Added selling_plan to item for ${productData.title}:`, {
            selling_plan: item.selling_plan,
            item: item
          });
        } else {
          console.warn(`⚠️ No selling_plan added to item for ${productData.title} - item will be one-time purchase`);
        }

        return item;
      });

      // Get cart drawer (same as product-form.js: this.cart)
      const cartDrawer = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const requestBody = { items };
      
      // Include sections in request (exactly like product-form.js line 36-40)
      // Make sure to include cart-icon-bubble for cart count updates
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        const sections = cartDrawer.getSectionsToRender().map((section) => section.id);
        // Ensure cart-icon-bubble is included
        if (!sections.includes('cart-icon-bubble')) {
          sections.push('cart-icon-bubble');
        }
        requestBody.sections = sections;
        requestBody.sections_url = window.location.pathname;
      } else {
        // Fallback: include cart-icon-bubble even if cart drawer doesn't have getSectionsToRender
        requestBody.sections = ['cart-icon-bubble'];
        requestBody.sections_url = window.location.pathname;
      }

      // Use fetchConfig exactly like product-form.js (line 31-33, 70-71)
      const config = typeof fetchConfig !== 'undefined' ? fetchConfig('javascript') : {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' }
      };
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.headers['Content-Type'] = 'application/json';
      config.body = JSON.stringify(requestBody);

      // Use routes.cart_add_url (same as product-form.js line 77)
      const cartAddUrl = (typeof routes !== 'undefined' && routes.cart_add_url) 
        ? routes.cart_add_url 
        : '/cart/add.js';

      // Log the final request body to debug selling plan issues
      console.log('📤 Final cart add request:', {
        url: cartAddUrl,
        items: items.map(item => ({
          id: item.id,
          quantity: item.quantity,
          selling_plan: item.selling_plan || 'NONE',
          properties: item.properties
        })),
        requestBody: requestBody
      });

      const response = await fetch(cartAddUrl, config);
      
      // Parse response as text first, then JSON (same as cart.js line 197-198)
      const responseText = await response.text();
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (e) {
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 200)}`);
      }

      // Check for errors (same as product-form.js line 80)
      if (result.status) {
        throw new Error(result.description || result.message || 'Failed to add to cart');
      }

      // Check if cart drawer exists before proceeding (same as product-form.js line 96)
      if (!cartDrawer) {
        // If no cart drawer, update cart icon bubble before redirecting
        if (result.sections && result.sections['cart-icon-bubble']) {
          this.updateCartIconBubble(result.sections['cart-icon-bubble']);
        } else {
          await this.refreshCartIconBubble();
        }
        window.location = (typeof routes !== 'undefined' && routes.cart_url) 
          ? routes.cart_url 
          : '/cart';
        return;
      }

      // Call renderContents on cart drawer directly (same as product-form.js line 122)
      // This will update both the cart drawer and cart-icon-bubble properly
      if (cartDrawer && typeof cartDrawer.renderContents === 'function' && result.sections && result.sections['cart-drawer']) {
        await cartDrawer.renderContents(result);
        console.log('✅ Cart drawer and cart icon bubble updated via renderContents');
        
        // Ensure empty state is properly handled when cart goes from empty to having items
        if (result.items && result.items.length > 0) {
          // Hide empty state elements
          const drawerInnerEmpty = document.querySelector('.drawer__inner-empty');
          if (drawerInnerEmpty) {
            drawerInnerEmpty.style.display = 'none';
          }
          
          // Show cart items container
          const itemParentContainer = document.querySelector('.item-parent-container');
          if (itemParentContainer) {
            itemParentContainer.style.display = '';
          }
          
          // Remove is-empty classes
          const cartDrawerElement = document.querySelector('cart-drawer');
          if (cartDrawerElement) {
            cartDrawerElement.classList.remove('is-empty');
          }
          
          const drawerInner = document.querySelector('.drawer__inner');
          if (drawerInner) {
            drawerInner.classList.remove('is-empty');
          }
          
          const cartDrawerItems = document.querySelector('cart-drawer-items');
          if (cartDrawerItems) {
            cartDrawerItems.classList.remove('is-empty');
          }
        }
      } else {
        // Fallback: refresh cart drawer manually if renderContents is not available or section not in response
        console.log('Refreshing cart drawer manually (renderContents not available or section missing)');
        await this.refreshCartDrawer();
        
        // Update cart icon bubble
        if (result.sections && result.sections['cart-icon-bubble']) {
          this.updateCartIconBubble(result.sections['cart-icon-bubble']);
        } else {
          await this.refreshCartIconBubble();
        }
      }

      // Trigger cart update events for other components that listen to it
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'bundle-builder',
          cartData: result
        });
        }
        
      // Open cart drawer after update
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
      
      // Initialize sliders with delay to ensure DOM is ready
      setTimeout(() => {
        // Initialize slider if it exists (same as cart.js onCartUpdate line 103-105)
        if (document.querySelector('.cart-drawer-upsell__slider')) {
          if (typeof initializeSlickSlider === 'function') {
            initializeSlickSlider('.cart-drawer-upsell__slider');
          }
        }
        
        // Reinitialize new cart drawer upsell slider
        if (typeof initializeNewUpsellSlider === 'function') {
          initializeNewUpsellSlider();
        }
        
        // Reinitialize cart drawer recommendations slider
        if (typeof initializeRecommendationsSlider === 'function') {
          initializeRecommendationsSlider();
        }
      }, 100);

      this.showNotification('Bundle added to cart!', 'success');

    } catch (error) {
      console.error('Add to cart error:', error);
      this.showNotification(error.message || 'Failed to add to cart', 'error');
    } finally {
      // Re-enable both buttons after operation completes (success or error)
      if (subscriptionButton) {
        subscriptionButton.disabled = false;
      }
      if (oneTimeButton) {
        oneTimeButton.disabled = false;
      }
      // Only remove loading class from the button that was clicked
      button.classList.remove('loading');
      // Restore original text to the text span (not the entire button)
      const textSpan = button.querySelector('.btn-text, .one-time-purchase-text');
      if (textSpan) {
        textSpan.textContent = originalText;
      } else {
        // Fallback if text span doesn't exist
      button.textContent = originalText;
      }
    }
  }

  // ==============================================
  // RENDER - Single update pipeline
  // ==============================================

  render() {
    console.log('🎨 Rendering UI, state:', {
      products: this.state.selectedProducts.size,
      items: this.getTotalItemCount()
    });

    this.renderProductCards();
    this.renderProductStack();
    this.renderProgressBar();
    this.renderPricing();
    // Always update subscription pricing when state changes
    this.renderSubscriptionPricing();
    this.renderButtons();
    
    console.log('🎨 Render complete');
  }

  renderProductCards() {
    // Update product card states using existing HTML structure
    const cards = this.querySelectorAll('.bundle-product-card');

    cards.forEach(card => {
      const variantId = parseInt(card.dataset.variantId);
      const product = this.state.selectedProducts.get(variantId);

      // Work with existing elements instead of replacing HTML
      const addButton = card.querySelector('.bundle-product-add-btn');
      const quantityControls = card.querySelector('.product-card__quantity-controls');
      const quantityDisplay = card.querySelector('.quantity-display');

      if (product && product.quantity > 0) {
        // Product in bundle - show quantity controls, hide add button
        card.classList.add('selected');
        if (addButton) addButton.classList.add('add-button-hidden');
        if (quantityControls) quantityControls.classList.remove('quantity-controls-hidden');
        if (quantityDisplay) quantityDisplay.textContent = product.quantity;
      } else {
        // Product not in bundle - show add button, hide quantity controls
        card.classList.remove('selected');
        if (addButton) addButton.classList.remove('add-button-hidden');
        if (quantityControls) quantityControls.classList.add('quantity-controls-hidden');
      }
    });
  }

  renderProductStack() {
    const stackContainer = this.elements.productStackItems;
    if (!stackContainer) return;

    const products = Array.from(this.state.selectedProducts.values());

    // Calculate pricing for total savings
    const pricing = this.calculatePricing();

    if (products.length === 0) {
      // Show empty state message
      stackContainer.innerHTML = '<div class="product-stack__empty-message">Your box is empty</div>';
      // Update savings amount to $0.00 when empty
      const savingsAmountEl = this.querySelector(`#product-stack-savings-${this.sectionId}`);
      if (savingsAmountEl) {
        savingsAmountEl.textContent = this.formatMoney(0);
      }
      return;
    }

    // Calculate pricing for each product
    const bundleDiscount = this.getCurrentBundleDiscount();

    stackContainer.innerHTML = products.map(product => {
      // Get the correct price based on subscription type
      // Use the same logic as calculatePricing() and renderSubscriptionPricing()
      const subscription = this.state.selectedSubscription;
      const oneTimePrice = product.price; // Original product price
      
      // Get all selling plans for this product to show pricing info
      const monthlySellingPlan = this.findMatchingSellingPlan(product, 'monthly');
      const threeMonthSellingPlan = this.findMatchingSellingPlan(product, '3monthly');
      const quarterlySellingPlan = this.findMatchingSellingPlan(product, 'quarterly');
      
      // Console log pricing information
      console.group(`💰 Pricing for: ${product.title}`);
      console.log('📦 Base Price (One-Time):', this.formatMoney(oneTimePrice));
      if (product.compareAtPrice && product.compareAtPrice > oneTimePrice) {
        const compareAtDiscount = ((product.compareAtPrice - oneTimePrice) / product.compareAtPrice * 100).toFixed(1);
        console.log('🏷️ Compare At Price:', this.formatMoney(product.compareAtPrice), `(${compareAtDiscount}% discount)`);
      } else {
        console.log('🏷️ Compare At Price: Not available');
      }
      if (monthlySellingPlan) {
        console.log('📅 Monthly Selling Plan:', {
          id: monthlySellingPlan.id,
          name: monthlySellingPlan.name,
          price: this.formatMoney(monthlySellingPlan.price),
          compare_at_price: monthlySellingPlan.compare_at_price ? this.formatMoney(monthlySellingPlan.compare_at_price) : 'N/A',
          variant_id: monthlySellingPlan.variant_id
        });
      } else {
        console.log('📅 Monthly Selling Plan: Not found');
      }
      if (threeMonthSellingPlan || quarterlySellingPlan) {
        const plan = threeMonthSellingPlan || quarterlySellingPlan;
        console.log('📅 3-Monthly Selling Plan:', {
          id: plan.id,
          name: plan.name,
          price: this.formatMoney(plan.price),
          compare_at_price: plan.compare_at_price ? this.formatMoney(plan.compare_at_price) : 'N/A',
          variant_id: plan.variant_id
        });
      } else {
        console.log('📅 3-Monthly Selling Plan: Not found');
      }
      console.log('🎯 Selected Subscription:', subscription?.type || 'onetime');
      console.groupEnd();
      
      // Use selling plan price when available (already includes subscription discount)
      // If no selling plan, use base price (not a subscription product)
      const productCompareAtPrice = product.compareAtPrice || 0;
      let productPrice = oneTimePrice;
      let sellingPlanCompareAtPrice = 0;
      
      if (subscription && subscription.type && subscription.type !== 'onetime') {
        const sellingPlanData = this.findMatchingSellingPlan(product, subscription.type);
        if (sellingPlanData && sellingPlanData.price) {
          // Use selling plan price (already includes subscription discount)
          productPrice = sellingPlanData.price;
          // Check if selling plan has compare_at_price (especially for 3-month subscriptions)
          if (sellingPlanData.compare_at_price && sellingPlanData.compare_at_price > sellingPlanData.price) {
            sellingPlanCompareAtPrice = sellingPlanData.compare_at_price;
          }
        }
        // If no selling plan found, productPrice remains oneTimePrice (not a subscription product)
      }
      
      // Determine which compare_at_price to use: selling plan compare_at_price takes priority, then product compare_at_price
      const compareAtPrice = sellingPlanCompareAtPrice > 0 ? sellingPlanCompareAtPrice : productCompareAtPrice;
      
      // Calculate line subtotal using selling plan price (if available) or base price
      const lineSubtotal = productPrice * product.quantity;
      // For comparison: use compare_at_price if available and higher, otherwise use base price
      const basePriceForComparison = compareAtPrice > oneTimePrice ? compareAtPrice : oneTimePrice;
      const baseLineSubtotal = basePriceForComparison * product.quantity;
      let lineDiscount = 0;
      let lineTotal = lineSubtotal;

      // Apply bundle discount to the line subtotal
      if (bundleDiscount > 0) {
        lineDiscount += this.calculateLineBundleDiscount(productPrice, product.quantity, bundleDiscount);
      }

      lineTotal = lineSubtotal - lineDiscount;
      
      // Calculate if we should show original price (when there's a discount from compare_at_price or bundle)
      // Always show compare_at_price if available, regardless of selling plan or bundle discounts
      const hasCompareAtDiscount = compareAtPrice > oneTimePrice;
      let originalPrice = null;
      
      if (hasCompareAtDiscount) {
        // Always show compare_at_price as original when available
        // Apply bundle discount to compare_at_price for display
        const compareAtLineSubtotal = compareAtPrice * product.quantity;
        const compareAtLineDiscount = bundleDiscount > 0
          ? this.calculateLineBundleDiscount(compareAtPrice, product.quantity, bundleDiscount)
          : 0;
        originalPrice = compareAtLineSubtotal - compareAtLineDiscount;
      } else if (baseLineSubtotal > lineSubtotal) {
        // Show base price as original if it's higher than current price (subscription savings)
        originalPrice = baseLineSubtotal;
      } else if (lineDiscount > 0) {
        // Show original before bundle discount
        originalPrice = lineSubtotal;
      }

      // Get product headline/subtitle from the product card if available
      const productCard = this.querySelector(`[data-variant-id="${product.variantId}"]`);
      let productTitle = product.title;
      let productSubtitle = '';

      if (productCard) {
        const headlineEl = productCard.querySelector('.product-card__headline');
        if (headlineEl) {
          productSubtitle = headlineEl.textContent.trim();
          // If headline exists, use it as subtitle and keep title as is
        } else {
          // If no headline, split title if it has common separators
          const titleParts = product.title.split(' - ');
          if (titleParts.length > 1) {
            productTitle = titleParts[0];
            productSubtitle = titleParts.slice(1).join(' - ');
          }
        }
      }

      return `
        <div class="stack-item" data-variant-id="${product.variantId}">
          <div class="stack-item-product">
            <div class="stack-item-image">
              <img src="${product.image || ''}" alt="${this.escapeHtml(product.title)}" loading="lazy">
            </div>
            <div class="stack-item-info">
              <div class="stack-item-title">${this.escapeHtml(productTitle)}</div>
              ${productSubtitle ? `<div class="stack-item-subtitle">${this.escapeHtml(productSubtitle)}</div>` : ''}
                <div class="stack-item-quantity-controls">
                  <button type="button" class="stack-item-quantity-btn stack-item-quantity-decrease" data-variant-id="${product.variantId}" aria-label="Decrease quantity">–</button>
                  <div class="stack-item-quantity-display">${product.quantity}</div>
                  <button type="button" class="stack-item-quantity-btn stack-item-quantity-increase" data-variant-id="${product.variantId}" aria-label="Increase quantity">+</button>
                </div>
            </div>
            <div class="stack-item-right">
                <div class="stack-item-price-section">
                ${originalPrice && originalPrice > lineTotal ? `<div class="stack-item-price-original">${this.formatMoney(originalPrice)}</div>` : ''}
                  <div class="stack-item-price-current">${this.formatMoney(lineTotal)}</div>
            </div>
            <button type="button" class="stack-item-remove" data-variant-id="${product.variantId}" aria-label="Remove product">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M10 11V17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M14 11V17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            </div>
          </div>
        </div>
      `;
    }).join('');

    // Update total savings amount
    const savingsAmountEl = this.querySelector(`#product-stack-savings-${this.sectionId}`);
    if (savingsAmountEl) {
      savingsAmountEl.textContent = this.formatMoney(pricing.totalSavings);
    }
  }

  renderProgressBar() {
    const totalItems = this.getTotalItemCount();
    
    // Update new bundle save progress bar
    let bundleSaveProgressFill = this.elements.bundleSaveProgressFill;
    const productCountButtons = this.elements.productCountButtons;

    // Ensure bundleSaveProgressFill is a DOM element (not an array or null)
    if (Array.isArray(bundleSaveProgressFill)) {
      bundleSaveProgressFill = bundleSaveProgressFill[0] || null;
    }
    
    // Fallback: try to find it directly if not cached
    if (!bundleSaveProgressFill || !bundleSaveProgressFill.style) {
      bundleSaveProgressFill = this.querySelector('.bundle-save-progress-fill');
    }

    if (bundleSaveProgressFill && bundleSaveProgressFill.style) {
      // Calculate progress based on tiers
      let progress = 0;
      
      if (this.tiers && this.tiers.length > 0) {
        const sortedTiers = [...this.tiers].sort((a, b) => a.minItems - b.minItems);
        
        // If no items, progress is 0
        if (totalItems === 0) {
          progress = 0;
        } else if (sortedTiers.length === 1) {
          // Single tier: 0% until reached, then 100%
          const tier = sortedTiers[0];
          if (totalItems >= tier.minItems) {
            progress = 100;
          } else {
            progress = (totalItems / tier.minItems) * 100;
          }
        } else {
          // Multiple tiers: calculate position based on which tier we're in
          // Tier positions are evenly distributed: 0%, 50%, 100% for 3 tiers
          // But we want them at: 33.33%, 66.66%, 100% (1/3, 2/3, 3/3)
          const tierPositions = sortedTiers.map((tier, index) => {
            // Special case: 3 tiers, middle tier (index 1) at 62% instead of 66.66%
            if (sortedTiers.length === 3 && index === 1) {
              return 62;
            }
            return ((index + 1) / sortedTiers.length) * 100;
          });
          
          // Find which tier we're currently in
          let currentTierIndex = -1;
          for (let i = sortedTiers.length - 1; i >= 0; i--) {
            if (totalItems >= sortedTiers[i].minItems) {
              currentTierIndex = i;
              break;
            }
          }
          
          if (currentTierIndex === -1) {
            // Before first tier - progress from 0% to first tier position
            const firstTier = sortedTiers[0];
            const firstTierPosition = tierPositions[0];
            progress = (totalItems / firstTier.minItems) * firstTierPosition;
          } else if (currentTierIndex === sortedTiers.length - 1) {
            // At or past final tier
            progress = 100;
          } else {
            // Between tiers - interpolate between current and next tier
            const currentTier = sortedTiers[currentTierIndex];
            const nextTier = sortedTiers[currentTierIndex + 1];
            const currentTierPosition = tierPositions[currentTierIndex];
            const nextTierPosition = tierPositions[currentTierIndex + 1];
            
            // Calculate how far we are between current and next tier
            const itemsInRange = nextTier.minItems - currentTier.minItems;
            const itemsAboveCurrent = totalItems - currentTier.minItems;
            const progressInRange = Math.min(itemsAboveCurrent / itemsInRange, 1);
            
            progress = currentTierPosition + (progressInRange * (nextTierPosition - currentTierPosition));
          }
        }
      }

      // Ensure progress is between 0 and 100
      progress = Math.max(0, Math.min(100, progress));

      // Update progress bar fill - use percentage directly
      bundleSaveProgressFill.style.width = `${progress}%`;
      bundleSaveProgressFill.setAttribute('data-progress', progress);

      // Update product count buttons - only mark the highest tier reached as active
      if (productCountButtons && productCountButtons.length > 0) {
        // First, remove selected class from all buttons
        productCountButtons.forEach((btn) => {
          btn.classList.remove('selected');
        });

        // Find the highest tier that's been reached
        if (this.tiers && this.tiers.length > 0) {
          const sortedTiers = [...this.tiers].sort((a, b) => a.minItems - b.minItems);
          let highestReachedTier = null;

          // Find the highest tier where totalItems >= minItems
          for (let i = sortedTiers.length - 1; i >= 0; i--) {
            if (totalItems >= sortedTiers[i].minItems) {
              highestReachedTier = sortedTiers[i];
              break;
            }
          }

          // Mark only the highest reached tier button as selected
          if (highestReachedTier) {
            productCountButtons.forEach((btn) => {
              const minItems = parseInt(btn.dataset.minItems);
              if (minItems === highestReachedTier.minItems) {
                btn.classList.add('selected');
              }
            });
          }
        } else {
          // Fallback: if no tiers, use the old logic
          productCountButtons.forEach((btn) => {
            const minItems = parseInt(btn.dataset.minItems);
            if (totalItems >= minItems) {
              btn.classList.add('selected');
            }
          });
        }
      }

      // Update discount labels - mark all reached tiers as bold
      const discountLabels = this.querySelectorAll('.discount-label');
      if (discountLabels && discountLabels.length > 0 && this.tiers && this.tiers.length > 0) {
        const sortedTiers = [...this.tiers].sort((a, b) => a.minItems - b.minItems);
        
        discountLabels.forEach((label) => {
          const tierIndex = parseInt(label.dataset.tierIndex);
          if (!isNaN(tierIndex) && tierIndex < sortedTiers.length) {
            const tier = sortedTiers[tierIndex];
            if (totalItems >= tier.minItems) {
              label.classList.add('reached');
            } else {
              label.classList.remove('reached');
            }
          }
        });
      }
    }

    // Update old progress bar if it exists (for backward compatibility)
    let progressFill = this.elements.progressBarFill;
    const milestoneMarkers = this.elements.milestoneMarkers;

    // Ensure progressFill is a DOM element (not an array or null)
    if (Array.isArray(progressFill)) {
      progressFill = progressFill[0] || null;
    }
    
    // Fallback: try to find it directly if not cached
    if (!progressFill || !progressFill.style) {
      progressFill = this.querySelector('.progress-bar-fill');
    }

    if (progressFill && progressFill.style) {
      let progress = 0;
      if (totalItems >= 4) {
        progress = 100;
      } else if (totalItems >= 3) {
        progress = 66;
      } else if (totalItems >= 2) {
        progress = 33;
      } else if (totalItems === 1) {
        progress = 16;
      }

      progressFill.style.width = `${progress}%`;
      progressFill.setAttribute('data-progress', progress);

      if (milestoneMarkers && milestoneMarkers.length > 0) {
        const requiredItems = [2, 3, 4];
        milestoneMarkers.forEach((marker, index) => {
          if (totalItems >= requiredItems[index]) {
            marker.classList.add('reached');
          } else {
            marker.classList.remove('reached');
          }
        });
      }
    }

    // Update progress messages (old structure, if it exists)
    this.updateProgressMessages(totalItems);
  }

  renderPricing() {
    const pricing = this.calculatePricing();

    // Update old structure elements if they exist (for compatibility)
    if (this.elements.subtotalElement) {
      this.elements.subtotalElement.textContent = this.formatMoney(pricing.subtotal);
    }

    if (this.elements.totalElement) {
      this.elements.totalElement.textContent = this.formatMoney(pricing.total);
    }

    if (this.elements.totalSavingsRow !== null && this.elements.totalSavingsRow !== undefined && this.elements.totalSavingsRow.length > 0) {
      if (pricing.totalSavings > 0) {
        this.elements.totalSavingsRow.style.display = 'block';
        if (this.elements.savingsAmount) {
          this.elements.savingsAmount.textContent = this.formatMoney(pricing.totalSavings);
        }
        if (this.elements.savingsPercent) {
          this.elements.savingsPercent.textContent = pricing.totalSavingsPercent.toFixed(0);
        }
      } else {
        this.elements.totalSavingsRow.style.display = 'none';
      }
    }

    // Update selected products list (old structure)
    if (this.elements.selectedProductsList) {
      this.updateSelectedProductsList();
    }
    
    // Note: Subscription pricing is handled in renderSubscriptionPricing()
    // which is called separately in render()
  }

  renderSubscriptionPricing() {
    const products = Array.from(this.state.selectedProducts.values());
    const bundleDiscount = this.getCurrentBundleDiscount();
    
    // Update subscription option card prices (show preview pricing for each option)
    // Find cards even if they're hidden (they're in the DOM, just display:none)
    const subscriptionCards = this.querySelectorAll('.subscription-option-card');
    
    subscriptionCards.forEach(card => {
      const radio = card.querySelector('.subscription-option-radio');
      if (!radio) return;
      
      const originalPriceEl = card.querySelector('.subscription-price-original');
      const discountedPriceEl = card.querySelector('.subscription-price-discounted');
      
      if (originalPriceEl && discountedPriceEl) {
        // Get subscription type from radio
        const subscriptionType = radio.value || radio.dataset.subscriptionType;
        const is3Month = subscriptionType === '3monthly' || subscriptionType === 'quarterly';
        const savingsEl = card.querySelector('.subscription-card-savings');
        const bundleLines = [];
        const compareAtLines = [];
        const baseLines = [];
        
        // Calculate subtotal using selling plan prices when available
        // Use base prices for comparison and savings calculation
        let subtotal = 0; // Price after subscription discount (from selling plans)
        let baseSubtotal = 0; // One-time/base prices for comparison
        let baseSubtotalForSavings = 0; // Base prices ONLY for products that have discounts (for accurate savings %)
        let compareAtSubtotal = 0; // For 3-month: compare_at_price total
        
        products.forEach(productData => {
          const quantity = productData.quantity;
          const oneTimePrice = productData.price; // Original product price (per unit)
          const compareAtPrice = productData.compareAtPrice || 0; // Compare at price if available (per unit)
          
          // If this is a subscription (not one-time), try to get selling plan price
          if (subscriptionType && subscriptionType !== 'onetime') {
            const sellingPlanData = this.findMatchingSellingPlan(productData, subscriptionType);
            if (sellingPlanData && sellingPlanData.price) {
              // Use selling plan price (already includes subscription discount)
              // For 3-month: selling plan price is already for 3 products
              subtotal += sellingPlanData.price * quantity;
              bundleLines.push({ unitPrice: sellingPlanData.price, quantity });
              
              // For base comparison: prioritize selling plan's compare_at_price (for 3-month it's already for 3 products)
              if (sellingPlanData.compare_at_price && sellingPlanData.compare_at_price > sellingPlanData.price) {
                // Use selling plan's compare_at_price as base (for 3-month: already for 3 products, e.g., $360)
                compareAtSubtotal += sellingPlanData.compare_at_price * quantity;
                baseSubtotal += sellingPlanData.compare_at_price * quantity;
                baseSubtotalForSavings += sellingPlanData.compare_at_price * quantity;
                compareAtLines.push({ unitPrice: sellingPlanData.compare_at_price, quantity });
                baseLines.push({ unitPrice: sellingPlanData.compare_at_price, quantity });
              } else {
                // No compare_at_price on selling plan
                // For 3-month: multiply by 3 to get base for 3 products
                // For monthly: use per-unit price
                const multiplier = is3Month ? 3 : 1;
                const basePricePerUnit = compareAtPrice > oneTimePrice ? compareAtPrice : oneTimePrice;
                const basePriceForComparison = basePricePerUnit * multiplier; // For 3-month: $120 * 3 = $360
                baseSubtotal += basePriceForComparison * quantity;
                baseLines.push({ unitPrice: basePriceForComparison, quantity });
                
                // Check if there's a discount (selling plan price is less than base price)
                if (sellingPlanData.price < basePriceForComparison) {
                  baseSubtotalForSavings += basePriceForComparison * quantity;
                }
              }
          } else {
              // No selling plan found - product doesn't support this subscription type
              // Use base price (it's not a subscription product)
              subtotal += oneTimePrice * quantity;
              bundleLines.push({ unitPrice: oneTimePrice, quantity });
              
              // Use compare_at_price for base comparison if available, otherwise use regular price
              const basePriceForComparison = compareAtPrice > oneTimePrice ? compareAtPrice : oneTimePrice;
              baseSubtotal += basePriceForComparison * quantity;
              baseLines.push({ unitPrice: basePriceForComparison, quantity });
              
              // Only include in savings if there's a compare_at_price discount
              if (compareAtPrice > oneTimePrice) {
                baseSubtotalForSavings += basePriceForComparison * quantity;
                compareAtLines.push({ unitPrice: basePriceForComparison, quantity });
              }
            }
          } else {
            // One-time purchase, use original product price
            subtotal += oneTimePrice * quantity;
            bundleLines.push({ unitPrice: oneTimePrice, quantity });
            
            // Use compare_at_price for base comparison if available, otherwise use regular price
            const basePriceForComparison = compareAtPrice > oneTimePrice ? compareAtPrice : oneTimePrice;
            baseSubtotal += basePriceForComparison * quantity;
            baseLines.push({ unitPrice: basePriceForComparison, quantity });
            
            // Only include in savings if there's a compare_at_price discount
            if (compareAtPrice > oneTimePrice) {
              baseSubtotalForSavings += basePriceForComparison * quantity;
              compareAtLines.push({ unitPrice: basePriceForComparison, quantity });
            }
          }
        });
        
        // Apply bundle discount to the subtotal
        const bundleDiscountAmount = this.calculateBundleDiscountFromLines(bundleLines, bundleDiscount);
        const afterBundleDiscount = subtotal - bundleDiscountAmount;
        const compareAtBundleDiscount = this.calculateBundleDiscountFromLines(compareAtLines, bundleDiscount);
        const baseBundleDiscount = this.calculateBundleDiscountFromLines(baseLines, bundleDiscount);
        
        // Calculate savings dynamically
        // Only count savings from products that actually have discounts
        // For 3-month: baseSubtotal already accounts for 3 products (compare_at * 3 or one-time * 3)
        // and subtotal is selling plan price (already for 3 products)
        const subscriptionSavings = baseSubtotal - subtotal; // Savings from selling plans and compare_at_price
        const totalSavings = subscriptionSavings + bundleDiscountAmount;
        
        // Calculate savings percent based on baseSubtotalForSavings (only products with discounts)
        // If no products have discounts, fall back to baseSubtotal
        const savingsBase = baseSubtotalForSavings > 0 ? baseSubtotalForSavings : baseSubtotal;
        const savingsPercent = savingsBase > 0 ? (totalSavings / savingsBase) * 100 : 0;
        
        // Ensure savings is never negative
        const finalTotalSavings = Math.max(0, totalSavings);
        const finalSavingsPercent = Math.max(0, savingsPercent);
        
        // Calculate compare_at_price discount for display in savings message
        let compareAtDiscountPercent = 0;
        let totalCompareAtPrice = 0;
        let totalRegularPrice = 0;
        
        products.forEach(productData => {
          const quantity = productData.quantity;
          const oneTimePrice = productData.price;
          const productCompareAtPrice = productData.compareAtPrice || 0;
          
          if (subscriptionType && subscriptionType !== 'onetime') {
            const sellingPlanData = this.findMatchingSellingPlan(productData, subscriptionType);
            
            if (sellingPlanData && sellingPlanData.price) {
              // Product has selling plan - check if selling plan has compare_at_price
              const sellingPlanCompareAt = sellingPlanData.compare_at_price || 0;
              
              if (sellingPlanCompareAt > sellingPlanData.price) {
                // Selling plan has compare_at_price discount
                totalCompareAtPrice += sellingPlanCompareAt * quantity;
                totalRegularPrice += sellingPlanData.price * quantity;
              }
            } else if (productCompareAtPrice > oneTimePrice) {
              // No selling plan, but product has compare_at_price discount
              totalCompareAtPrice += productCompareAtPrice * quantity;
              totalRegularPrice += oneTimePrice * quantity;
            }
          } else if (productCompareAtPrice > oneTimePrice) {
            // One-time purchase with compare_at_price discount
            totalCompareAtPrice += productCompareAtPrice * quantity;
            totalRegularPrice += oneTimePrice * quantity;
          }
        });
        
        if (totalCompareAtPrice > 0 && totalCompareAtPrice > totalRegularPrice) {
          // Calculate discount percentage from compare_at_price
          compareAtDiscountPercent = ((totalCompareAtPrice - totalRegularPrice) / totalCompareAtPrice) * 100;
        }
        
        // Determine what to show
        let originalPrice = 0;
        let cardTotal = 0;
        
        if (subscriptionType === '3monthly' || subscriptionType === 'quarterly') {
          // 3-month: Show compare_at_price as original (if available), price as discounted
          if (compareAtSubtotal > 0 && compareAtSubtotal > subtotal) {
            // We have compare_at_price and it's higher than price
            originalPrice = compareAtSubtotal - compareAtBundleDiscount; // compare_at_price with bundle discount
            cardTotal = afterBundleDiscount; // price with bundle discount
            originalPriceEl.textContent = this.formatMoney(originalPrice);
            originalPriceEl.style.display = '';
          } else if (baseSubtotal > subtotal) {
            // No compare_at_price, but base price is higher (showing subscription savings)
            originalPrice = baseSubtotal; // base price with bundle discount
            cardTotal = afterBundleDiscount; // selling plan price with bundle discount
            originalPriceEl.textContent = this.formatMoney(originalPrice);
            originalPriceEl.style.display = '';
          } else {
            // No discount to show
            cardTotal = afterBundleDiscount;
            originalPriceEl.style.display = 'none';
          }
        } else if (subscriptionType && subscriptionType !== 'onetime') {
          // Monthly or other subscription: Show base price as original, selling plan price as discounted
          if (baseSubtotal > subtotal) {
            // We have subscription savings from selling plan
            originalPrice = baseSubtotal; // base price with bundle discount
            cardTotal = afterBundleDiscount; // selling plan price with bundle discount
            originalPriceEl.textContent = this.formatMoney(originalPrice);
            originalPriceEl.style.display = '';
          } else {
            // No subscription savings (product doesn't have selling plan)
            cardTotal = afterBundleDiscount;
            originalPriceEl.style.display = 'none';
          }
        } else {
          // One-time: Just show bundle discount
          if (bundleDiscount > 0) {
            originalPrice = subtotal; // Before bundle discount
            cardTotal = afterBundleDiscount; // After bundle discount
            originalPriceEl.textContent = this.formatMoney(originalPrice);
            originalPriceEl.style.display = '';
          } else {
            cardTotal = afterBundleDiscount;
            originalPriceEl.style.display = 'none';
          }
        }
        
        discountedPriceEl.textContent = this.formatMoney(cardTotal);
        
        // Update savings message dynamically
        // Calculate the actual discount percentage based on what's displayed (originalPrice vs cardTotal)
        // Priority: compare_at_price discount > displayed price difference
        if (savingsEl) {
          if (compareAtDiscountPercent > 0) {
            // Show compare_at_price discount (from selling plan or product)
            savingsEl.textContent = `Save Extra ${compareAtDiscountPercent.toFixed(0)}%`;
          } else if (originalPrice > 0 && originalPrice > cardTotal) {
            // Calculate discount based on the actual displayed prices
            // This ensures the percentage matches what users see visually
            const displayedDiscountPercent = ((originalPrice - cardTotal) / originalPrice) * 100;
            savingsEl.textContent = `Save Extra ${displayedDiscountPercent.toFixed(0)}%`;
          } else if (finalSavingsPercent > 0) {
            // Fallback: Show total savings (selling plan + bundle discounts)
            savingsEl.textContent = `Save Extra ${finalSavingsPercent.toFixed(0)}%`;
          } else {
            savingsEl.textContent = '';
          }
        }
      }
    });
    
    // Update toggle text with dynamically calculated maximum savings
    this.updateSubscriptionToggleTextFromCards();
    
    // Calculate current total with selected subscription
    const currentPricing = this.calculatePricing();
    
    // Update subscription add to cart total (based on currently selected subscription)
    const subscriptionTotalEl = this.querySelector('.subscription-total-amount');
    const subscriptionTotalInlineEl = this.querySelector('.subscription-total-amount-inline');
    
    if (subscriptionTotalEl) {
      subscriptionTotalEl.textContent = this.formatMoney(currentPricing.total);
    }
    
    if (subscriptionTotalInlineEl) {
      // Update inline total in the button
      subscriptionTotalInlineEl.textContent = this.formatMoney(currentPricing.total);
    }
    
    // Update one-time purchase price (no subscription discount, only bundle discount)
    const oneTimePriceEl = this.querySelector('.one-time-purchase-price');
    if (oneTimePriceEl) {
      // Calculate one-time price using base product prices (not selling plan prices)
      let oneTimeSubtotal = 0;
      const oneTimeLines = [];
      products.forEach(productData => {
        oneTimeSubtotal += productData.price * productData.quantity;
        oneTimeLines.push({ unitPrice: productData.price, quantity: productData.quantity });
      });
      
      // Apply bundle discount only
      const oneTimeBundleDiscountAmount = this.calculateBundleDiscountFromLines(oneTimeLines, bundleDiscount);
      const oneTimeTotal = oneTimeSubtotal - oneTimeBundleDiscountAmount;
      
      oneTimePriceEl.textContent = this.formatMoney(oneTimeTotal);
    }
    
  }

  renderButtons() {
    const totalItems = this.getTotalItemCount();
    
    // Get minimum required items from the first discount tier, fallback to config
    let minRequired = this.config.minProducts || 2;
    if (this.tiers && this.tiers.length > 0) {
      // Sort tiers by minItems and get the first one
      const sortedTiers = [...this.tiers].sort((a, b) => a.minItems - b.minItems);
      minRequired = sortedTiers[0].minItems;
    }

    // Update buy button (old structure - only if it exists)
    let buyButton = this.elements.buyButton;
    if (!buyButton) {
      buyButton = this.querySelector('.buy-now-button');
    }
    
    if (buyButton) {
      buyButton.disabled = totalItems < minRequired;
    }

    // Update subscription add to cart button
    const subscriptionAddToCartBtn = this.querySelector(`#subscription-add-to-cart-${this.sectionId}`);
    if (subscriptionAddToCartBtn) {
      subscriptionAddToCartBtn.disabled = totalItems < minRequired;
    }

    // Update one-time purchase button (always visible, enable/disable based on items)
    const oneTimePurchaseBtn = this.querySelector(`#one-time-purchase-${this.sectionId}`);
    if (oneTimePurchaseBtn) {
      oneTimePurchaseBtn.disabled = totalItems < minRequired;
    }

    // Update add to cart button (old structure)
    if (this.elements.addToCartButton) {
      this.elements.addToCartButton.disabled = totalItems < minRequired;
    }
  }

  // Removed updateBuyNowPricing - pricing is now handled in renderSubscriptionPricing()
  // which updates subscription cards, add to cart button, and one-time purchase button

  // ==============================================
  // CALCULATIONS
  // ==============================================

  getTotalItemCount() {
    return Array.from(this.state.selectedProducts.values())
      .reduce((sum, product) => sum + product.quantity, 0);
  }

  getCurrentBundleDiscount() {
    const totalItems = this.getTotalItemCount();
    if (totalItems === 0) return 0;

    // Use tiers from blocks configuration
    if (this.tiers && this.tiers.length > 0) {
      // Sort tiers by minItems (ascending)
      const sortedTiers = [...this.tiers].sort((a, b) => a.minItems - b.minItems);
      
      // Find the highest tier that the user qualifies for
      let applicableTier = null;
      for (let i = sortedTiers.length - 1; i >= 0; i--) {
        const tier = sortedTiers[i];
        if (totalItems >= tier.minItems) {
          // Check if we're within maxItems (if maxItems is 0 or undefined, it's unlimited)
          if (tier.maxItems === 0 || tier.maxItems === undefined || tier.maxItems === 999 || totalItems <= tier.maxItems) {
            applicableTier = tier;
            break;
          }
        }
      }
      
      if (applicableTier) {
        return applicableTier.discountPercentage;
      }
    }
    
    // Fallback to hardcoded values if no tiers configured
    if (totalItems >= 4) {
      return 15;
    } else if (totalItems >= 3) {
      return 10;
    } else if (totalItems >= 2) {
      return 5;
    }
    
    return 0;
  }

  calculatePricing() {
    const products = Array.from(this.state.selectedProducts.values());
    const subscription = this.state.selectedSubscription;
    const is3Month = subscription?.type === '3monthly' || subscription?.type === 'quarterly';
    const bundleLines = [];
    
    // Calculate subtotal using product prices from Shopify
    // Use selling plan prices when available (they already include subscription discount)
    // If no selling plan, use base product price (not a subscription product)
    let subtotal = 0; // Price after subscription discount (from selling plans)
    let baseSubtotal = 0; // Base prices for comparison and savings calculation
    let baseSubtotalForSavings = 0; // Base prices ONLY for products that have discounts (for accurate savings %)
    
    products.forEach(productData => {
      const oneTimePrice = productData.price; // Original product price (per unit)
      const compareAtPrice = productData.compareAtPrice || 0; // Compare at price if available (per unit)
      const quantity = productData.quantity;
      let hasDiscount = false; // Track if this product has any discount
      
      // If subscription is selected and not one-time, try to get selling plan price
      if (subscription && subscription.type && subscription.type !== 'onetime') {
        const sellingPlanData = this.findMatchingSellingPlan(productData, subscription.type);
        if (sellingPlanData && sellingPlanData.price) {
          // Use selling plan price (already includes subscription discount)
          // For 3-month: selling plan price is already for 3 products
          subtotal += sellingPlanData.price * quantity;
          bundleLines.push({ unitPrice: sellingPlanData.price, quantity });
          
          // For base comparison: prioritize selling plan's compare_at_price (for 3-month it's already for 3 products)
          // If no selling plan compare_at_price, use product compare_at_price * multiplier OR one-time price * multiplier
          if (sellingPlanData.compare_at_price && sellingPlanData.compare_at_price > sellingPlanData.price) {
            // Use selling plan's compare_at_price as base (for 3-month: already for 3 products, e.g., $360)
            baseSubtotal += sellingPlanData.compare_at_price * quantity;
            baseSubtotalForSavings += sellingPlanData.compare_at_price * quantity;
            hasDiscount = true;
          } else {
            // No compare_at_price on selling plan
            // For 3-month: multiply by 3 to get base for 3 products
            // For monthly: use per-unit price
            const multiplier = is3Month ? 3 : 1;
            const basePricePerUnit = compareAtPrice > oneTimePrice ? compareAtPrice : oneTimePrice;
            const basePriceForComparison = basePricePerUnit * multiplier; // For 3-month: $120 * 3 = $360
            baseSubtotal += basePriceForComparison * quantity;
            
            // Check if there's a discount (selling plan price is less than base price)
            // For 3-month: compare $300 (selling plan) vs $360 (base for 3 products)
            if (sellingPlanData.price < basePriceForComparison) {
              baseSubtotalForSavings += basePriceForComparison * quantity;
              hasDiscount = true;
            }
          }
        } else {
          // No selling plan found - product doesn't support this subscription type
          // Use base price (it's not a subscription product)
          subtotal += oneTimePrice * quantity;
          bundleLines.push({ unitPrice: oneTimePrice, quantity });
          // Use compare_at_price for base comparison if available, otherwise use regular price
          const basePriceForComparison = compareAtPrice > oneTimePrice ? compareAtPrice : oneTimePrice;
          baseSubtotal += basePriceForComparison * quantity;
          
          // Only include in savings calculation if there's a compare_at_price discount
          if (compareAtPrice > oneTimePrice) {
            baseSubtotalForSavings += basePriceForComparison * quantity;
            hasDiscount = true;
          }
        }
      } else {
        // One-time purchase, use original product price
        subtotal += oneTimePrice * quantity;
        bundleLines.push({ unitPrice: oneTimePrice, quantity });
        // Use compare_at_price for base comparison if available, otherwise use regular price
        const basePriceForComparison = compareAtPrice > oneTimePrice ? compareAtPrice : oneTimePrice;
        baseSubtotal += basePriceForComparison * quantity;
        
        // Only include in savings calculation if there's a compare_at_price discount
        if (compareAtPrice > oneTimePrice) {
          baseSubtotalForSavings += basePriceForComparison * quantity;
          hasDiscount = true;
        }
      }
    });

    const bundleDiscount = this.getCurrentBundleDiscount();
    const bundleDiscountAmount = this.calculateBundleDiscountFromLines(bundleLines, bundleDiscount);
    const total = subtotal - bundleDiscountAmount;

    // Calculate total savings: difference between base price and final price
    // Only count savings from products that actually have discounts
    // For 3-month: baseSubtotal already accounts for 3 products (compare_at * 3 or one-time * 3)
    // and subtotal is selling plan price (already for 3 products)
    const subscriptionSavings = baseSubtotal - subtotal; // Total savings from selling plans and compare_at_price
    const totalSavings = subscriptionSavings + bundleDiscountAmount;
    
    // Calculate savings percent based on baseSubtotalForSavings (only products with discounts)
    // If no products have discounts, fall back to baseSubtotal
    const savingsBase = baseSubtotalForSavings > 0 ? baseSubtotalForSavings : baseSubtotal;
    const totalSavingsPercent = savingsBase > 0 ? (totalSavings / savingsBase) * 100 : 0;
    
    // Ensure savings is never negative
    const finalTotalSavings = Math.max(0, totalSavings);
    const finalTotalSavingsPercent = Math.max(0, totalSavingsPercent);

    // Debug logging
    console.log('💰 Pricing calculation:', {
      is3Month: is3Month,
      baseSubtotal: this.formatMoney(baseSubtotal),
      baseSubtotalForSavings: this.formatMoney(baseSubtotalForSavings),
      subtotal: this.formatMoney(subtotal),
      bundleDiscount: `${bundleDiscount}%`,
      bundleDiscountAmount: this.formatMoney(bundleDiscountAmount),
      subscriptionType: subscription?.type || 'onetime',
      subscriptionSavings: this.formatMoney(subscriptionSavings),
      total: this.formatMoney(total),
      totalSavings: this.formatMoney(finalTotalSavings),
      totalSavingsPercent: `${finalTotalSavingsPercent.toFixed(1)}%`,
      note: is3Month ? '3-month: base and selling plan prices are for 3 products' : 'Monthly: prices are per unit'
    });

    return {
      subtotal, // Price after subscription discount (from selling plans)
      baseSubtotal, // One-time prices for comparison
      bundleDiscount,
      bundleDiscountAmount,
      subscriptionSavings, // Savings from selling plans
      total,
      totalSavings: finalTotalSavings,
      totalSavingsPercent: finalTotalSavingsPercent
    };
  }

  findMatchingSellingPlan(productData, subscriptionType) {
    if (!productData.sellingPlans || productData.sellingPlans.length === 0) {
      return null;
    }

    const subscriptionMapping = {
      'monthly': ['30 Days', 'Monthly', 'month', '30 days', '30day', '30-day'],
      '3monthly': [
        '3 Months', '3 months', '3 month', '3month', '3-month', '3months',
        'Quarterly', 'quarterly', 'quarter', '3 Months Subscription', '3 month subscription',
        '3-month subscription', '3months subscription', 'quarterly subscription'
      ],
      'quarterly': [
        '3 Months', '3 months', '3 month', '3month', '3-month', '3months',
        'Quarterly', 'quarterly', 'quarter', '3 Months Subscription', '3 month subscription',
        '3-month subscription', '3months subscription', 'quarterly subscription'
      ],
      'onetime': null
    };

    const patterns = subscriptionMapping[subscriptionType?.toLowerCase()];
    if (!patterns) return null;

    for (const sellingPlan of productData.sellingPlans) {
      const planName = sellingPlan.name.toLowerCase();
      for (const pattern of patterns) {
        if (planName.includes(pattern.toLowerCase())) {
          console.log(`✅ Matched selling plan "${sellingPlan.name}" with pattern "${pattern}" for subscription type "${subscriptionType}"`);
          return sellingPlan;
        }
      }
    }

    console.warn(`⚠️ No selling plan matched for subscription type "${subscriptionType}"`, {
      availablePlans: productData.sellingPlans.map(sp => sp.name),
      searchedPatterns: patterns
    });
    return null;
  }

  // ==============================================
  // HELPER METHODS
  // ==============================================

  updateCartIconBubble(sectionHtml) {
    try {
      // Use the same method as cart-drawer.js getSectionInnerHTML
      // This matches how cart-drawer.js updates the cart-icon-bubble
      const getSectionInnerHTML = (html, selector = '.shopify-section') => {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
      };
      
      // Extract the innerHTML from the section (same as cart-drawer.js line 99-100)
      const newContent = getSectionInnerHTML(sectionHtml, '.shopify-section');
      
      // Update cart-icon-bubble element using getElementById (same as cart-drawer.js line 84)
      const cartIconBubbleElement = document.getElementById('cart-icon-bubble');
      if (cartIconBubbleElement) {
        cartIconBubbleElement.innerHTML = newContent;
        console.log('✅ Cart icon bubble updated via getElementById');
      } else {
        // Fallback: try querySelector if getElementById doesn't work
        const desktopCartIcon = document.querySelector('#cart-icon-bubble');
        if (desktopCartIcon) {
          desktopCartIcon.innerHTML = newContent;
          console.log('✅ Cart icon bubble updated via querySelector (fallback)');
        } else {
          console.warn('Cart icon bubble element not found in DOM');
          return;
        }
      }
      
      // Also update mobile cart icon bubble if it exists
      const mobileCartIcon = document.getElementById('mobile-cart-icon-bubble') || 
                            document.querySelector('#mobile-cart-icon-bubble');
      if (mobileCartIcon) {
        mobileCartIcon.innerHTML = newContent;
      }
    } catch (error) {
      console.error('Error updating cart icon bubble:', error);
    }
  }

  async refreshCartIconBubble() {
    try {
      const cartUrl = (typeof routes !== 'undefined' && routes.cart_url) ? routes.cart_url : '/cart';
      const sectionsResponse = await fetch(`${cartUrl}?section_id=cart-icon-bubble&t=${Date.now()}`);
      
      if (!sectionsResponse.ok) {
        console.warn('Failed to fetch cart icon bubble section');
        return;
      }
      
      const sectionsHtml = await sectionsResponse.text();
      const sectionsDoc = new DOMParser().parseFromString(sectionsHtml, 'text/html');
      
      // Try to find the cart icon bubble - it might be in a section wrapper
      let newCartIconBubble = sectionsDoc.querySelector('#cart-icon-bubble');
      if (!newCartIconBubble) {
        const sectionWrapper = sectionsDoc.querySelector('[id*="shopify-section-cart-icon-bubble"]') ||
                              sectionsDoc.querySelector('.shopify-section');
        if (sectionWrapper) {
          newCartIconBubble = sectionWrapper.querySelector('#cart-icon-bubble');
        }
      }
      
      if (newCartIconBubble) {
        // Get the entire inner HTML (including icon and count bubble, or empty if cart is empty)
        const newContent = newCartIconBubble.innerHTML;
        
        // Update desktop cart icon bubble - replace entire innerHTML
        const desktopCartIcon = document.querySelector('#cart-icon-bubble');
        if (desktopCartIcon) {
          desktopCartIcon.innerHTML = newContent;
        }
        
        // Update mobile cart icon bubble (if it exists)
        const mobileCartIcon = document.querySelector('#mobile-cart-icon-bubble');
        if (mobileCartIcon) {
          mobileCartIcon.innerHTML = newContent;
        }
        
        console.log('Cart icon bubble refreshed');
      } else {
        console.warn('Cart icon bubble not found in fetched section HTML');
      }
    } catch (error) {
      console.error('Error refreshing cart icon bubble:', error);
    }
  }

  async refreshCartDrawer() {
    try {
      const cartUrl = (typeof routes !== 'undefined' && routes.cart_url) ? routes.cart_url : '/cart';
      const sectionsResponse = await fetch(`${cartUrl}?section_id=cart-drawer&t=${Date.now()}`);
      
      if (!sectionsResponse.ok) {
        console.warn('Failed to fetch cart drawer section');
        return;
      }
      
      const sectionsHtml = await sectionsResponse.text();
      const sectionsDoc = new DOMParser().parseFromString(sectionsHtml, 'text/html');
      
      // Replace cart-drawer-items element
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      const newCartDrawerItems = sectionsDoc.querySelector('cart-drawer-items');
      
      if (cartDrawerItems && newCartDrawerItems) {
        cartDrawerItems.replaceWith(newCartDrawerItems);
        
        // Remove is-empty classes
        const newCartDrawerItemsAfterReplace = document.querySelector('cart-drawer-items');
        if (newCartDrawerItemsAfterReplace) {
          newCartDrawerItemsAfterReplace.classList.remove('is-empty');
        }
        
        const cartDrawerElement = document.querySelector('cart-drawer');
        if (cartDrawerElement) {
          cartDrawerElement.classList.remove('is-empty');
        }
        
        const drawerInner = document.querySelector('.drawer__inner');
        if (drawerInner) {
          drawerInner.classList.remove('is-empty');
        }
        
        const drawerInnerEmpty = document.querySelector('.drawer__inner-empty');
        if (drawerInnerEmpty) {
          drawerInnerEmpty.style.display = 'none';
        }
        
        const itemParentContainer = document.querySelector('.item-parent-container');
        if (itemParentContainer) {
          itemParentContainer.style.display = '';
        }
      }
      
      // Replace cart-drawer__footer if it exists
      const cartDrawerFooter = document.querySelector('.cart-drawer__footer');
      const newCartDrawerFooter = sectionsDoc.querySelector('.cart-drawer__footer');
      if (cartDrawerFooter && newCartDrawerFooter) {
        cartDrawerFooter.replaceWith(newCartDrawerFooter);
      }
      
    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
    }
  }

  updateProgressMessages(totalItems) {
    // Only for old structure with progress messages
    if (!this.elements.progressMessage) return;

    const currentDiscount = this.getCurrentBundleDiscount();
    const subscriptionDiscount = this.state.selectedSubscription?.discount || 0;
    const totalDiscount = currentDiscount + subscriptionDiscount;

    const sortedTiers = [...this.tiers].sort((a, b) => a.minItems - b.minItems);
    const nextTier = sortedTiers.find(tier => totalItems < tier.minItems);

    let mainMessage = '';
    let subMessage = '';

    if (totalItems === 0) {
      mainMessage = '🎁 Add products to unlock bundle discounts!';
      if (nextTier) {
        subMessage = `Add ${nextTier.minItems} items for ${nextTier.discountPercentage}% bundle discount`;
      }
    } else if (nextTier) {
      const itemsNeeded = nextTier.minItems - totalItems;
      mainMessage = `<span class="next-tier">Add ${itemsNeeded} more to unlock ${nextTier.discountPercentage}% bundle discount</span>`;
      if (totalDiscount > 0) {
        subMessage = `<span class="unlocked">✓ Current: ${currentDiscount}% bundle`;
        if (subscriptionDiscount > 0) {
          subMessage += ` + ${subscriptionDiscount}% subscription = ${totalDiscount}% total`;
        }
        subMessage += `</span>`;
      }
    } else if (currentDiscount > 0) {
      mainMessage = `<span class="unlocked">🎉 Maximum ${currentDiscount}% bundle discount unlocked!</span>`;
      if (subscriptionDiscount > 0) {
        subMessage = `Total savings: ${currentDiscount}% + ${subscriptionDiscount}% = <strong>${totalDiscount}%</strong>`;
      }
    }

    this.elements.progressMessage.innerHTML = mainMessage;
    if (this.elements.progressSubtext) {
      this.elements.progressSubtext.innerHTML = subMessage;
    }
  }

  updateSelectedProductsList() {
    // Only for old structure
    if (!this.elements.selectedProductsList) return;

    const products = Array.from(this.state.selectedProducts.values());

    if (products.length === 0) {
      this.elements.selectedProductsList.innerHTML = '<p class="empty-message">No products selected yet</p>';
      return;
    }

    const html = products.map(product => {
      const lineTotal = product.price * product.quantity;
      return `
        <div class="selected-product-item" data-variant-id="${product.variantId}">
          <div class="selected-product-info">
            <span class="selected-product-title">${this.escapeHtml(product.title)}</span>
            <span class="selected-product-quantity">Qty: ${product.quantity}</span>
            <span class="selected-product-price">${this.formatMoney(product.price)} × ${product.quantity} = ${this.formatMoney(lineTotal)}</span>
          </div>
          <button type="button" class="selected-product-remove" data-variant-id="${product.variantId}">×</button>
        </div>
      `;
    }).join('');

    this.elements.selectedProductsList.innerHTML = html;
  }

  showNotification(message, type = 'error') {
    const toast = document.createElement('div');
    toast.className = `bundle-builder-toast bundle-builder-toast--${type}`;
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      bottom: 2rem;
      right: 2rem;
      background: ${type === 'success' ? '#2ecc71' : '#e74c3c'};
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10000;
      font-weight: 600;
      animation: slideIn 0.3s ease-out;
    `;

    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.animation = 'slideOut 0.3s ease-in';
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  normalizeUnitPrice(value) {
    let numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      return 0;
    }
    if (!Number.isInteger(numericValue)) {
      numericValue = Math.round(numericValue * 100);
    }
    return Math.round(numericValue);
  }

  calculateLineBundleDiscount(unitPrice, quantity, discountPercent) {
    if (!discountPercent || discountPercent <= 0 || !quantity || quantity <= 0) {
      return 0;
    }
    const normalizedPrice = this.normalizeUnitPrice(unitPrice);
    if (normalizedPrice <= 0) {
      return 0;
    }
    const perUnitDiscount = Math.floor((normalizedPrice * discountPercent) / 100);
    return perUnitDiscount * quantity;
  }

  calculateBundleDiscountFromLines(lines, discountPercent) {
    if (!Array.isArray(lines) || lines.length === 0 || !discountPercent || discountPercent <= 0) {
      return 0;
    }

    return lines.reduce((total, line) => {
      if (!line) return total;
      const unitPrice = line.unitPrice ?? line.price ?? 0;
      const quantity = line.quantity ?? 1;
      return total + this.calculateLineBundleDiscount(unitPrice, quantity, discountPercent);
    }, 0);
  }

  formatMoney(cents, formatOverride) {
    const value = typeof cents === 'string' ? cents.replace(/[^0-9-]/g, '') : cents;
    const centsValue = Number.isFinite(Number(value)) ? Number(value) : 0;
    const formatString = formatOverride || this.moneyFormat || '{{amount}}';
    const shopifyGlobal = typeof window !== 'undefined' ? window.Shopify : undefined;

    if (shopifyGlobal && typeof shopifyGlobal.formatMoney === 'function') {
      try {
        return shopifyGlobal.formatMoney(Math.round(centsValue), formatString);
      } catch (error) {
        console.warn('Shopify.formatMoney failed, using fallback formatter.', error);
      }
    }

    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (!Number.isFinite(number)) {
        return '0';
      }
      const fixedNumber = Number(number).toFixed(Math.max(0, precision));
      const parts = fixedNumber.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      const centsPart = parts[1] ? `${decimal}${parts[1]}` : '';
      return `${dollars}${centsPart}`;
    };

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = formatString.match(placeholderRegex);
    const placeholder = match ? match[1] : 'amount';
    const amount = centsValue / 100;
    let formattedValue;

    switch (placeholder) {
      case 'amount_no_decimals':
        formattedValue = formatWithDelimiters(amount, 0);
        break;
      case 'amount_with_comma_separator':
        formattedValue = formatWithDelimiters(amount, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formattedValue = formatWithDelimiters(amount, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        formattedValue = formatWithDelimiters(amount, 0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formattedValue = formatWithDelimiters(amount, 2, "'", '.');
        break;
      case 'amount':
      default:
        formattedValue = formatWithDelimiters(amount, 2);
        break;
    }

    if (match) {
      return formatString.replace(placeholderRegex, formattedValue);
    }

    // Final fallback using Intl for locales without placeholders
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.currencyCode || 'USD'
      }).format(amount);
    } catch {
      const fallbackSymbol = (this.currencyCode === 'GBP' && '£')
        || (this.currencyCode === 'EUR' && '€')
        || '$';
      return `${fallbackSymbol}${amount.toFixed(2)}`;
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Register custom element
console.log('📦 Registering bundle-builder custom element...');
if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
  console.log('✅ bundle-builder registered successfully!');
} else {
  console.log('⚠️ bundle-builder already registered');
}
